import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../../components/common/DashboardLayout';
import AdminNav from '../../components/admin/AdminNav';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import ImageDropZone from '../../components/common/ImageDropZone';
import MkInitiatives, { IdeasList } from '../../components/admin/MkInitiatives';
import PersonalFacebookViews from '../../components/admin/PersonalFacebookViews';
import MovesLog from '../../components/admin/MovesLog';
import ContestsPage from '../../components/admin/ContestsPage';
import BrandLibrary, { CopyButton } from '../../components/admin/BrandLibrary';
import { fireHearts } from '../../utils/celebrate';
import './salesMarketing.css';

/**
 * Sales & Marketing Control Room — built exactly from CB's v4 design
 * (365reset-sales-marketing-control-room (2).html, Sept 28, 2026), which Jenn
 * went through page by page so it works with how her brain works: numbers
 * first, one question per card, every card opens its page, funnels /
 * up-and-down bars / thermometers only, never a stack of sideways bars.
 *
 * Jenn, 2026-09-28: "build it exactly as is and use the words In
 * Development for stuff we don't yet have ... Build how it will look with
 * false data and say it's in development." So every card is one of two kinds:
 *   - Live: real numbers from /api/admin/sales-marketing/overview
 *     (first-party tracking since Sept 28 + what members told us at signup).
 *   - In development: CB's example numbers, labelled "In development ·
 *     example numbers" right on the card so an example is never mistaken
 *     for a real number.
 * The reputation alert is always shown quietly — no red — real or example
 * (Jenn: "That read really hurts my head").
 */

// CB's chart colours, with Jenn's exact brand terracotta and sage.
const C = { terra: '#C4714A', terra2: '#D9A07E', sand: '#E3D3BC', light: '#F0EBE3', forest: '#4A6355', forest2: '#6E8B79', amber: '#C8922E', red: '#B4492B' };

const PAGES = [
  ['home', 'Dashboard'], ['posts', "This week's posts"], ['nextposts', "Next week's posts"], ['brand', 'Brand & vision'], ['mylinks', 'My links page'], ['goals', 'Goals & plan'], ['members', 'Members'], ['referrals', 'Referrals'],
  ['visibility', 'Visibility'], ['leads', 'Interested people'], ['events', 'LM Live Events'], ['magnets', 'Lead magnets'], ['contests', 'Contests'],
  ['lab', 'Mini-lab'], ['market', 'Market pulse'], ['proof', 'Proof library'], ['inventory', "What's out there"], ['tools', 'Tools'],
];
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const HOURS = ['6–9 am', '9–12', '12–3 pm', '3–6 pm', '6–9 pm', '9 pm+'];

// ─── Building blocks — same shapes as CB's funnel(), vbars() and thermo() ──────

function Status({ live }) {
  return live
    ? <span className="stat live"><span className="dot l" />Live</span>
    : <span className="stat dev"><span className="dot d" />In development · example numbers</span>;
}

function Card({ live, go, onGo, className = '', style, children }) {
  const clickable = go && onGo;
  return (
    <div
      className={`card ${clickable ? 'go' : ''} ${className}`}
      style={style}
      onClick={clickable ? () => onGo(go) : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter') onGo(go); } : undefined}
    >
      {live !== undefined && <div><Status live={live} /></div>}
      {children}
    </div>
  );
}

/** steps: [[label, number | null]] — null = that step isn't measured yet, shown as "In development". */
function Funnel({ steps }) {
  const n = steps.length;
  const cols = [C.sand, C.terra2, C.terra, '#A9563A', C.forest2, C.forest];
  return (
    <div className="funnel">
      {steps.map(([label, value], i) => {
        const w = 100 - i * (58 / Math.max(n - 1, 1));
        const col = i === n - 1 ? C.forest : cols[Math.min(i, cols.length - 2)];
        const prev = i > 0 ? steps[i - 1][1] : null;
        const rate = i > 0 && value != null && prev ? Math.round((value / prev) * 100) : null;
        return (
          <React.Fragment key={label}>
            {i > 0 && rate != null && <div className="fr">{rate}% continue</div>}
            {i > 0 && rate == null && <div className="fr">&nbsp;</div>}
            <div className={`fs ${value == null ? 'dev' : ''}`} style={{ width: `${w}%`, background: col, color: i === 0 ? '#2B2313' : '#fff' }}>
              <b>{value == null ? 'In development' : value}</b><span>{label}</span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

/** series: [{ name, color, colors?, vals }] */
/** A null value = not measured yet: shown as "—" with no bar, never as a made-up number. */
function VBars({ labels, series, max, fmt = (v) => v, h = 170, keyItems }) {
  const top = max || Math.max(1, ...series.flatMap(s => s.vals).filter(v => v != null));
  return (
    <div>
      <div className="vbars" style={{ height: h }}>
        {labels.map((l, i) => (
          <div className="vb" key={l + i}>
            {series.map((s, si) => {
              const v = s.vals[i];
              return (
                <div className="col1" key={si}>
                  <em>{v == null ? '—' : fmt(v)}</em>
                  <i style={{ height: v == null ? 0 : `${Math.max(2, (v / top) * 82)}%`, background: (s.colors && s.colors[i]) || s.color }} />
                </div>
              );
            })}
          </div>
        ))}
      </div>
      <div className="vlab">{labels.map((l, i) => <span key={l + i}>{l}</span>)}</div>
      {series.length > 1 && <div className="key">{series.map(s => <span key={s.name} style={{ '--c': s.color }}>{s.name}</span>)}</div>}
      {keyItems && <div className="key">{keyItems.map(([name, c]) => <span key={name} style={{ '--c': c }}>{name}</span>)}</div>}
    </div>
  );
}

function Thermo({ val, goal, color = C.terra, sm, goalLabel, sub }) {
  const pct = Math.min(100, (val / goal) * 100);
  return (
    <div className={`thermo ${sm ? 'th-sm' : ''}`} style={{ '--c': color }}>
      <div className="th-goal">Goal {goalLabel || goal}</div>
      <div className="tube">
        {[1, 2, 3].map(t => <span key={t} className="tick" style={{ bottom: `${t * 25}%` }} />)}
        <div className="lvl" style={{ height: `${Math.max(3, pct)}%` }} />
      </div>
      <div className="bulb">{val}</div>
      <div className="lbl" style={{ textAlign: 'center' }}>{sub || `${Math.round(pct)}% of goal`}</div>
    </div>
  );
}

function OneBar({ label, pct }) {
  return (
    <div className="one" style={{ marginTop: 14 }}>
      <div className="t"><span>{label}</span><b>{pct == null ? '—' : `${pct}%`}</b></div>
      <div className="track"><div className="fill" style={{ width: `${pct || 0}%`, background: 'var(--terra)' }} /></div>
    </div>
  );
}

const Sticky = ({ tone = '', title, children }) => <div className={`sticky ${tone}`}><b>{title}</b><br />{children}</div>;

// ─── Tagged link builder (live) — follows the link rules given to CB ──────────
const slug = (s) => (s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

function LinkBuilder() {
  const [platform, setPlatform] = useState('facebook');
  const [account, setAccount] = useState('social');
  const [campaign, setCampaign] = useState(() => {
    const d = new Date(); const day = (d.getDay() + 6) % 7; d.setDate(d.getDate() - day);
    return `wk-${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  const [post, setPost] = useState('');
  const [copied, setCopied] = useState(null);
  const [short, setShort] = useState(null);
  const [shortErr, setShortErr] = useState(null);
  const [recent, setRecent] = useState([]);
  const [find, setFind] = useState('');
  // Jenn, 2026-10-13: the short links list stays folded away until she opens it.
  const [showLinks, setShowLinks] = useState(false);
  const url = `https://365reset.com/?utm_source=${platform}&utm_medium=${account}&utm_campaign=${slug(campaign) || 'campaign'}&utm_content=${slug(post) || 'post-name'}`;
  const loadRecent = () => api.get('/admin/sales-marketing/short-links').then(r => setRecent(r.data.links)).catch(() => {});
  useEffect(() => { loadRecent(); }, []);
  useEffect(() => { setShort(null); setShortErr(null); }, [url]);
  const copy = async (text, which) => {
    try { await navigator.clipboard.writeText(text); setCopied(which); setTimeout(() => setCopied(null), 2000); } catch { /* copy by hand */ }
  };
  // Short link (2026-09-29): 365reset.com/go/<post-name> -> this full tagged link.
  const makeShort = async () => {
    setShortErr(null);
    if (!slug(post)) { setShortErr('Give the post a name first.'); return; }
    try {
      const r = await api.post('/admin/sales-marketing/short-links', { targetUrl: url, code: slug(post) });
      setShort(r.data.shortUrl); loadRecent();
    } catch (e) { setShortErr(e.response?.data?.error || 'Could not make the short link.'); }
  };
  return (
    <>
      <div className="ws">
        <label className="fld">Platform
          <select value={platform} onChange={e => setPlatform(e.target.value)}>
            {['facebook', 'instagram', 'threads', 'tiktok', 'pinterest', 'youtube', 'linkedin', 'substack', 'email'].map(p => <option key={p}>{p}</option>)}
          </select>
        </label>
        <label className="fld">Whose account
          <select value={account} onChange={e => setAccount(e.target.value)}>
            <option value="social">365reset's account</option>
            <option value="social-jenn">Jenn's own account</option>
          </select>
        </label>
        <label className="fld">Campaign<input value={campaign} onChange={e => setCampaign(e.target.value)} /></label>
        <label className="fld">Post name<input value={post} onChange={e => setPost(e.target.value)} placeholder="mon-kinda-chic-fb" /></label>
      </div>
      <div className="note" style={{ fontFamily: 'ui-monospace,Menlo,monospace', wordBreak: 'break-all' }}>{url}</div>
      <div className="row" style={{ marginTop: 10 }}>
        <button type="button" className="btn p s" onClick={makeShort}>Make short link</button>
        <button type="button" className="btn s" onClick={() => copy(url, 'long')}>{copied === 'long' ? 'Copied ✓' : 'Copy the long link'}</button>
      </div>
      {shortErr && <div className="note">{shortErr}</div>}
      {short && (
        <div className="note" style={{ background: 'var(--forest-soft)' }}>
          <b style={{ fontSize: 16 }}>{short.replace('https://', '')}</b>
          <div className="row" style={{ marginTop: 8 }}><button type="button" className="btn p s" onClick={() => copy(short, 'short')}>{copied === 'short' ? 'Copied ✓' : 'Copy short link'}</button></div>
          <div className="qs" style={{ margin: '6px 0 0' }}>Use this one in the post. It opens the full tagged link, so tracking still works.</div>
        </div>
      )}
      {recent.length > 0 && (
        <div style={{ marginTop: 14 }}>
        <button type="button" className="btn" onClick={() => setShowLinks(v => !v)} aria-expanded={showLinks}>
          {showLinks ? '▾ Hide your short links' : `▸ Show your short links (${recent.length})`}
        </button>
        {showLinks && <>
        <label className="fld" style={{ marginTop: 10 }}>Find a short link<input value={find} onChange={e => setFind(e.target.value)} placeholder="e.g. tt, or mon-whatif" /></label>
        <div className="tw" style={{ marginTop: 8 }}><table>
          <thead><tr><th>Your short links</th><th className="n">Clicks</th><th /></tr></thead>
          <tbody>{recent.filter(l => !find.trim() || l.code.includes(find.trim().toLowerCase())).map(l => (
            <tr key={l.code}><td>365reset.com/go/{l.code}</td><td className="n">{l.clicks}</td>
              <td><button type="button" className="btn s" onClick={() => copy(`https://365reset.com/go/${l.code}`, l.code)}>{copied === l.code ? 'Copied ✓' : 'Copy'}</button></td></tr>
          ))}</tbody>
        </table></div>
        </>}
        </div>
      )}
    </>
  );
}

// ─── Spreadsheet download of every tagged post (live) ─────────────────────────
function downloadPostsCsv(posts) {
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const lines = [['Post', 'Platform', 'Campaign', 'Visits', 'Started signing up', 'Joined'].map(esc).join(',')]
    .concat(posts.map(p => [p.post, p.platform, p.campaign, p.visits, p.started, p.joins].map(esc).join(',')));
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `365reset-top-posts-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}



export default function AdminSalesMarketing() {
  // Patti and Trish see Sales & Marketing (2026-10-13) but not Jenn's personal
  // Facebook numbers or the Affiliate Control Room — those stay affiliate-manager only.
  const { user } = useAuth();
  const isAffiliateManager = !!user?.can_manage_affiliates;
  const initial = (() => { const h = (window.location.hash || '').slice(1); return PAGES.some(([id]) => id === h) ? h : 'home'; })();
  const [page, setPage] = useState(initial);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  // Goals & plan + timer (To-Do 10, 2026-09-29) — Jenn's own goals, focus, strategy, hours, costs, decisions.
  const [planData, setPlanData] = useState(null);
  const loadPlan = useCallback(() => {
    api.get('/admin/sales-marketing/plan').then(r => setPlanData(r.data)).catch(() => {});
  }, []);

  // Mini-lab (2026-10-06): every post we've published, with our own tracking numbers.
  const [lab, setLab] = useState(null);
  const loadLab = useCallback(() => {
    api.get('/admin/sales-marketing/lab').then(r => setLab(r.data)).catch(() => setLab({ error: true, weeks: [], experiments: [] }));
  }, []);
  useEffect(() => { if (page === 'lab' && !lab) loadLab(); }, [page, lab, loadLab]);

  useEffect(() => {
    api.get('/admin/sales-marketing/overview')
      .then(r => setData(r.data))
      .catch(err => setError(err.response?.data?.error || 'Could not load the Sales & Marketing numbers.'));
    loadPlan();
  }, [loadPlan]);

  const go = useCallback((p) => {
    setPage(p);
    try { window.history.replaceState(null, '', `#${p}`); window.scrollTo(0, 0); } catch { /* fine */ }
  }, []);

  const d = data;
  const f = d?.marketingFunnel;
  const strangerSteps = [
    ['Visited 365reset.com', f ? f.visited : null], ['Came back or read more', f ? f.cameBack : null],
    ['Gave their email', null], ['Engaged before signing up', null],
    ['Started signing up', f ? f.started : null], ['Joined', f ? f.joined : null],
  ];
  // Referral funnel (live Oct 8 2026): link visits → started → joined. Member referrals are typed names, so they only count at Joined.
  const rf = d?.referrals?.funnel;
  const referralSteps = [['Visited from a referral link', rf ? rf.visited : null], ['Started signing up', rf ? rf.started : null], ['Joined (affiliate links and members)', rf ? rf.joinedAll : null]];
  const referralRate = rf && rf.visited ? Math.round((rf.joinedFromLinks / rf.visited) * 1000) / 10 : null;
  const strangerRate = f && f.visited ? Math.round((f.joined / f.visited) * 1000) / 10 : null;
  const topPosts = (d?.posts || []).slice(0, 5);
  const heard = (d?.howHeard || []).filter(h => h.answer !== 'Other' || h.n > 0);
  const byDayKeys = [['Visits', C.terra2], ['Days someone joined', C.forest]];
  const byDayVals = (d?.byDay || DAYS.map(() => ({ visits: 0, joined: false })));
  // Members & engagement — real since 2026-09-28's "fill it with the real numbers we know now".
  const mem = d?.members;
  const plans = mem?.plans;
  const eng = mem?.engagementThisWeek;
  const engVals = eng ? [eng.liveEvent, eng.replay, eng.checkin, eng.lesson, eng.hive] : [null, null, null, null, null];
  // Opens/clicks are counted from emails sent after tracking began (To-Do 3, Sept 28); before the first tracked send they stay "In development".
  const em = mem?.email;
  const tracking = em && em.today.trackedSent > 0;
  const nudgeSteps = [['Nudge sent', mem ? mem.nudgesSentToday : null], ['Opened', tracking ? em.today.opened : null], ['Read to the end', null], ['Clicked into My Hub', tracking ? em.today.clickedHub : null]];
  const opensNote = <div className="note" style={{ marginTop: 10 }}>Some email apps open emails by themselves, so "Opened" can run a little high. "Read to the end" can't be measured in email.</div>;
  const planVals = plans ? [plans.monthly, plans.foundingPaid, plans.foundingComped, plans.freePlan, plans.archived] : [0, 0, 0, 0, 0];
  const cg = planData?.plan?.campaign_goals;
  const ladder = planData?.plan?.goal_ladder;
  const breakEvenGoal = ladder?.breakEven || mem?.breakEven.goal || 147;
  const stickyPct = cg?.stickyPct ?? 80;
  const stickyGoal = plans ? Math.max(1, Math.ceil(plans.active * (stickyPct / 100))) : 1;
  const notYet = (what) => <div className="note" style={{ marginTop: 10 }}><b>Not measured yet:</b> {what}. Shown as "—" until it is.</div>;

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto">
        <AdminNav />
        <div className="smcr">
          <header className="bar">
            <div className="bar-top">
              <h1 className="font-serif"><span>365reset ·</span> Sales &amp; Marketing</h1>
              <div className="who">Team365</div>
            </div>
            <nav>
              {PAGES.map(([id, label]) => (
                <a key={id} href={`#${id}`} className={page === id ? 'on' : ''} onClick={(e) => { e.preventDefault(); go(id); }}>{label}</a>
              ))}
            </nav>
          </header>
          <div className="example">
            <span><span className="dot l" />Live · real numbers, counted since {d?.trackingStarted ? 'Sept 28, 2026' : '…'}</span>
            <span><span className="dot d" />In development · example numbers, for the design only</span>
          </div>
          {error && <div className="note" style={{ marginTop: 14 }}>{error}</div>}
          {planData && page !== 'home' && <TimerBar planData={planData} onChange={loadPlan} />}

          {/* =============================== DASHBOARD =============================== */}
          {page === 'home' && (
            <section>
              {/* Jenn, 2026-09-29: the timer and this week's focus side by side, focus as a
                  checkable list stacked top to bottom (linear layouts are hard on her
                  cognitive load); the example reputation alert moved to the bottom. */}
              <div className="grid g2" style={{ marginTop: 16 }}>
                {planData ? <TimerBar planData={planData} onChange={loadPlan} boxed /> : <div className="card" />}
                <FocusCard planData={planData} onChange={loadPlan} onGo={go} />
              </div>

              <h2 className="ptitle">Is 365reset sustainable?</h2>
              <p className="pq">Break-even is 147 monthly paying members. Click any card to open its page.</p>
              <div className="grid gA">
                <Card live go="members" onGo={go}>
                  <p className="q">Monthly paying members</p><p className="qs">Toward break-even</p>
                  <Thermo val={mem?.breakEven.monthly ?? 0} goal={breakEvenGoal} color={C.forest} sub={mem ? `${mem.breakEven.monthly} of ${breakEvenGoal} · ${Math.max(0, breakEvenGoal - mem.breakEven.monthly)} to go` : '…'} />
                  <div className="more" style={{ textAlign: 'center' }}>Members →</div>
                </Card>
                <div className="grid" style={{ gap: 16 }}>
                  <Card live go="members" onGo={go}>
                    <p className="q">Members by plan</p><p className="qs">Everyone in 365reset right now</p>
                    <VBars labels={['Monthly', 'Founding · paid', 'Founding · comp\'d', 'Comp\'d', 'Archived']} series={[{ color: C.forest, colors: [C.forest, C.terra, C.terra2, C.amber, C.sand], vals: planVals }]} h={150} />
                    <div className="more">Members →</div>
                  </Card>
                  <Card live go="members" onGo={go}>
                    <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div><p className="q">The December decision</p><p className="qs">Free plans ending, by month</p></div>
                      <div style={{ textAlign: 'right', paddingRight: 22 }}><div className="bign" style={{ fontSize: 38 }}>{Math.max(0, Math.ceil((new Date(new Date().getFullYear(), 11, 31) - new Date()) / 864e5))}</div><div className="lbl">days to Dec 31</div></div>
                    </div>
                    <VBars labels={['Oct', 'Nov', 'Dec', 'Jan', 'Later']} series={[{ color: C.terra, colors: [C.terra2, C.terra2, C.red, C.terra2, C.terra2], vals: mem ? mem.freeEndingByMonth : [0, 0, 0, 0, 0] }]} h={130} />
                    <div className="more">Plan the December campaign →</div>
                  </Card>
                </div>
              </div>

              <h2 className="ptitle">Our campaigns</h2>
              <p className="pq">Five campaigns always running. Each thermometer is its goal.</p>
              <div className="grid g5">
                <Card live go="visibility" onGo={go}>
                  <p className="q">Everyday posts</p><p className="qs">New members from tagged posts this quarter</p>
                  <Thermo sm val={d?.everydayPosts?.joins ?? 0} goal={cg?.everydayPosts || 25} sub={`${d?.everydayPosts?.joins ?? 0} of ${cg?.everydayPosts || 25}`} />
                </Card>
                <Card live go="referrals" onGo={go}><p className="q">Affiliates</p><p className="qs">Joins this quarter</p><Thermo sm val={d?.affiliatesThisQuarter ?? 0} goal={cg?.affiliates || 10} color={C.forest} sub={`${d?.affiliatesThisQuarter ?? 0} of ${cg?.affiliates || 10}`} /></Card>
                <Card live={false} go="magnets" onGo={go}><p className="q">Lead magnet (Q4)</p><p className="qs">Joins from this quarter's campaign</p><Thermo sm val={0} goal={cg?.leadMagnet || 15} color={C.amber} sub="Starts Oct" /></Card>
                <Card live go="members" onGo={go}><p className="q">Sticky members</p><p className="qs">Members active this week: My Hub, emails, live events (calendar links too)</p><Thermo sm val={mem?.weeklyActive.count ?? 0} goal={stickyGoal} color={C.forest} goalLabel={`${stickyPct}%`} sub={`${mem?.weeklyActive.pct ?? 0}% active this week`} /></Card>
                <Card live={false} go="members" onGo={go}><p className="q">December renewals</p><p className="qs">Free-plan members who choose to pay</p><Thermo sm val={0} goal={cg?.decemberRenewals || 27} color={C.red} sub={`0 of ${cg?.decemberRenewals || 27} · Dec`} /></Card>
              </div>

              {/* Live 2026-10-21 (Jenn: "pull the affiliate forecast out of development and make it work"). */}
              <Card live go="referrals" onGo={go} style={{ marginTop: 16 }}>
                <p className="q">Affiliate forecast</p><p className="qs">People affiliates and partners sent us in the last 30 days, and what we expect to land next month</p>
                {!d?.affiliateForecast ? <p className="qs">Loading…</p> : (() => { const f = d.affiliateForecast; const exp = f.expectedNextMonth; return <>
                  <VBars labels={['Came through their link', 'Saw their page', 'Reached the price', 'Joined this month']} series={[{ color: C.terra, colors: [C.sand, C.terra2, C.terra, C.forest], vals: [f.visited, f.viewedLanding, f.reachedPrice, f.joinedThisMonth] }]} h={150} />
                  <p style={{ marginTop: 10, fontSize: 15 }}><b>Expected to join next month: {exp >= 1 ? `about ${Math.round(exp)}` : exp > 0 ? 'less than 1' : '0'}</b></p>
                  <div className="note"><b>How it's estimated:</b> people in play × {f.rateSource === 'own'
                    ? `our own rate: ${f.ratePct}% of people sent by affiliates have joined.`
                    : `${f.ratePct}%, the usual rate for visitors from social media (Market pulse). We switch to our own rate once affiliates have brought ${f.minOwnJoins} joins through their links (${f.ownJoins} so far).`}</div>
                </>; })()}
              </Card>

              <h2 className="ptitle">Are members engaged and staying?</h2>
              <p className="pq">Retention is the first sale.</p>
              <div className="grid g2">
                <Card live go="members" onGo={go}><p className="q">Today's nudge</p><p className="qs">From the daily email into My Hub</p>
                  <Funnel steps={nudgeSteps} />{opensNote}</Card>
                <Card live go="members" onGo={go}><p className="q">How members engaged this week</p><p className="qs">Members who did each, at least once</p>
                  <VBars labels={['Live event', 'Replay', 'Daily check-in', 'Lesson', 'Hive post']} series={[{ color: C.forest, vals: engVals }]} />
                  <div className="note" style={{ marginTop: 10 }}>Live events and replays count from Sept 28, but only when a member presses Join or opens a replay inside My Hub. Most members join from the shared Google Calendar or a reminder, and those joins aren't counted yet, so the live event number runs low.</div></Card>
              </div>
              <Card live go="members" onGo={go} style={{ marginTop: 16 }}>
                <p className="q">Staying power</p><p className="qs">Percent of members</p>
                <VBars labels={['Weekly active', 'Check-in done this quarter', 'Opened weekly summary', 'Likely to renew']} series={[{ color: C.forest, colors: [C.forest, C.forest2, C.terra2, C.terra], vals: mem ? [mem.weeklyActive.pct, mem.quarterlyCheckins.pct, em?.weeklySummaryOpenPct ?? null, null] : [null, null, null, null] }]} max={100} fmt={v => `${v}%`} h={160} />
                {notYet(em?.weeklySummaryOpenPct == null ? 'weekly summary opens (counting starts with the next one sent), and how likely each member is to renew' : 'how likely each member is to renew')}
              </Card>

              <h2 className="ptitle">Are new people finding us and buying?</h2>
              <p className="pq">Levels of interest, from stranger to member, this month.</p>
              <div className="grid g2">
                <Card live go="visibility" onGo={go}>
                  <p className="q">From stranger to member</p><p className="qs">All marketing, this month</p>
                  <Funnel steps={strangerSteps} />
                  <OneBar label="Stranger-to-member conversion" pct={strangerRate} />
                </Card>
                <Card live go="referrals" onGo={go}>
                  <p className="q">Referrals</p><p className="qs">Members and affiliates bringing people in, this month</p>
                  <Funnel steps={referralSteps} />
                  <OneBar label="Referral-link conversion" pct={referralRate} />
                </Card>
              </div>

              <h2 className="ptitle">What's working, and when?</h2>
              <p className="pq">The needle for each tactic, and when prospects act.</p>
              <div className="grid g2">
                {/* Live 2026-10-21 (Jenn: "show us what is moving people to the page until we start getting joins"). */}
                <Card live go="lab" onGo={go}><p className="q">Is the needle moving?</p><p className="qs">People who reached 365reset.com, by where they came from · last week and this week (Sun–Sat)</p>
                  {!d ? <p className="qs">Loading…</p> : !(d.needle || []).length ? <p className="qs">No visits yet this week or last.</p> : <>
                    <VBars labels={d.needle.map(r => NEEDLE_SHORT[r.source] || r.source)} series={[{ name: 'Last week', color: C.sand, vals: d.needle.map(r => r.lastWeek) }, { name: 'This week', color: C.forest, vals: d.needle.map(r => r.thisWeek) }]} h={150} />
                    <div style={{ marginTop: 10, fontSize: 13, lineHeight: 1.6 }}>
                      {d.needle.filter(r => r.thisWeek || r.joined).map(r => (
                        <div key={r.source}><b>{r.source}</b>: {r.thisWeek} this week{r.reachedPrice ? ` · ${r.reachedPrice} reached the price` : ''}{r.joined ? ` · ${r.joined} joined` : ''}</div>
                      ))}
                    </div>
                    <div className="note" style={{ marginTop: 10 }}>Real visits only, not link clicks, so robots aren't counted. "Came straight to the site" includes our own team's visits.</div>
                  </>}</Card>
                <Card live go="lab" onGo={go}><p className="q">When do prospects act?</p><p className="qs">Visits and joins by day, this month</p>
                  <VBars labels={DAYS} series={[{ color: C.terra2, colors: byDayVals.map(x => (x.joined ? C.forest : C.terra2)), vals: byDayVals.map(x => x.visits) }]} h={150} keyItems={byDayKeys} /></Card>
              </div>
              {/* In development until reputation monitoring exists — kept, but not in prime position. */}
              {/* Always quiet, real or example — Jenn, 2026-09-28. */}
              <div className="quiet" style={{ marginTop: 24 }} onClick={() => go('visibility')} role="button" tabIndex={0}>
                <div><Status live={false} /><b>Reputation alert · example</b>
                  <span>An article questioning online wellness memberships is circulating in Canada. Open it to see it and plan a reply.</span></div>
              </div>
            </section>
          )}

          {/* =============================== GOALS & PLAN =============================== */}
          {page === 'goals' && (
            <GoalsPage planData={planData} reload={loadPlan} d={d} />
          )}

          {/* =============================== MEMBERS =============================== */}
          {page === 'members' && (
            <section>
              <h2 className="ptitle">Members</h2>
              <p className="pq">Who's here, what they pay, when they decide, and who's engaged.</p>
              <div style={{ marginBottom: 8 }}><Status live /></div>
              <div className="grid g5">
                {/* Plans follow Jenn's pricing rules (2026-09-28): not founding = CA$20.58/month. */}
                <div className="plan hl"><div className="count">Monthly</div><div className="bign">{plans ? plans.monthly : '…'}</div><div className="pr">CA$20.58 / month</div><div className="rl">Counts toward break-even</div></div>
                <div className="plan"><div className="count">Founding · paid</div><div className="bign">{plans ? plans.foundingPaid : '…'}</div><div className="pr">CA$49.95 / year</div><div className="rl">Same rate through 2028 · retail from Jan 1, 2029</div></div>
                <div className="plan"><div className="count">Founding · comp'd</div><div className="bign">{plans ? plans.foundingComped : '…'}</div><div className="pr">Free in 2026</div><div className="rl">Then CA$49.95 / year for 2027 and 2028</div></div>
                <div className="plan"><div className="count">Comp'd</div><div className="bign">{plans ? plans.freePlan : '…'}</div><div className="pr">Free until their one-year date</div><div className="rl">Then renew at the regular price</div></div>
                <div className="plan"><div className="count">Archived</div><div className="bign">{plans ? plans.archived : '…'}</div><div className="pr">No longer active</div><div className="rl">Didn't continue · win-back pool</div></div>
              </div>
              <div className="grid gA" style={{ marginTop: 16 }}>
                <Card live><p className="q">Break-even</p><p className="qs">Monthly paying members</p><Thermo val={mem?.breakEven.monthly ?? 0} goal={breakEvenGoal} color={C.forest} sub={mem ? `${Math.max(0, breakEvenGoal - mem.breakEven.monthly)} more to break even` : '…'} /></Card>
                <Card live><p className="q">Revenue by plan</p><p className="qs">What each plan brings in per year (CA$)</p>
                  <VBars labels={['Monthly', 'Founding · paid', 'Founding · comp\'d (2027, if all renew)']} series={[{ color: C.forest, colors: [C.forest, C.terra, C.terra2], vals: mem ? [mem.revenueByPlan.monthly, mem.revenueByPlan.foundingPaid, mem.revenueByPlan.foundingComped2027] : [0, 0, 0] }]} fmt={v => `$${v}`} h={150} /></Card>
              </div>
              <h2 className="ptitle">The December decision</h2>
              <p className="pq">Most free plans started January 1. In {Math.max(0, Math.ceil((new Date(new Date().getFullYear(), 11, 31) - new Date()) / 864e5))} days, those members decide whether to stay and pay.</p>
              <div className="grid g2">
                <Card live><p className="q">Free plans ending, by month</p><p className="qs">Founding comp'd and comp'd members</p>
                  <VBars labels={['Oct', 'Nov', 'Dec', 'Jan', 'Later']} series={[{ color: C.terra, colors: [C.terra2, C.terra2, C.red, C.terra2, C.terra2], vals: mem ? mem.freeEndingByMonth : [0, 0, 0, 0, 0] }]} /></Card>
                <Card live><p className="q">How ready are they?</p><p className="qs">Free-plan members by how engaged they are now · in My Hub this week, this month, or not</p>
                  <VBars labels={['Ready to stay', 'Sometimes in', 'Quiet']} series={[{ color: C.forest, colors: [C.forest, C.amber, C.red], vals: mem ? mem.freePlanReadiness : [0, 0, 0] }]} />
                  <div className="note"><b>Why it matters:</b> engaged members are the ones most likely to say yes in December. Quiet ones need a reason to come back before then.</div></Card>
              </div>
              <Card live={false} style={{ marginTop: 16 }}><p className="q">December campaign workspace</p><p className="qs">Where we plan the strategy together</p>
                <div className="grid g4">
                  <Sticky tone="f" title="Ready to stay">Thank them, and make paying easy</Sticky>
                  <Sticky title="Sometimes in">A personal invitation to one live event</Sticky>
                  <Sticky tone="t" title="Quiet">A note from Jenn: what would make this worth it?</Sticky>
                  <Sticky title="Amplifiers">Their story in the Proof library</Sticky>
                </div>
                <div className="row" style={{ marginTop: 12 }}><button type="button" className="btn p">Build the December campaign →</button><button type="button" className="btn">See everyone whose plan ends in December</button></div></Card>
              <Card live style={{ marginTop: 16 }}><p className="q">Archived members: win-back</p><p className="qs">People who didn't continue, by when they left</p>
                <VBars labels={['0–3 months', '3–6 months', '6–9 months', '9+ months']} series={[{ color: C.sand, colors: [C.terra2, C.terra2, C.sand, C.sand], vals: mem?.leaving?.archivedByLeft || [0, 0, 0, 0] }]} h={140} />
                {mem?.leaving?.leftUnknown > 0 && <div className="note" style={{ marginTop: 10 }}>{mem.leaving.leftUnknown} archived {mem.leaving.leftUnknown === 1 ? 'member left' : 'members left'} before we recorded leaving dates, so {mem.leaving.leftUnknown === 1 ? "they aren't" : "they aren't"} shown above.</div>}
                <div className="row" style={{ marginTop: 12 }}><button type="button" className="btn">Decide on a win-back</button></div></Card>

              <h2 className="ptitle">Engagement</h2>
              <p className="pq">Every way members show up: daily, weekly, in My Hub and outside it.</p>
              <div className="grid g2">
                <Card live><p className="q">Daily nudge → My Hub</p><p className="qs">Today</p><Funnel steps={nudgeSteps} />{opensNote}</Card>
                <Card live><p className="q">Daily ways they engage in My Hub</p><p className="qs">Members who did each this week</p>
                  <VBars labels={['Live event', 'Replay', 'Daily check-in', 'Lesson', 'Hive post or comment']} series={[{ color: C.forest, vals: engVals }]} />
                  <div className="note" style={{ marginTop: 10 }}>Live events and replays count from Sept 28, but only when a member presses Join or opens a replay inside My Hub. Most members join from the shared Google Calendar or a reminder, and those joins aren't counted yet, so the live event number runs low.</div></Card>
              </div>
              <div className="grid g3" style={{ marginTop: 16 }}>
                <Card live><p className="q">My Hive vs Facebook group</p><p className="qs">Members posting or commenting, by week</p>
                  <VBars labels={mem ? mem.hiveByWeek.map(w => w.label) : ['', '', '', '']} series={[{ name: 'My Hive', color: C.forest, vals: mem ? mem.hiveByWeek.map(w => w.n) : [0, 0, 0, 0] }, { name: 'Facebook group (not measured yet)', color: C.terra2, vals: [null, null, null, null] }]} h={150} />
                  <div className="note"><b>The goal:</b> My Hive grows and the Facebook group becomes a lead and nurture group. Facebook may not share group numbers, so that count may be entered weekly by hand.</div></Card>
                <Card live={false}><p className="q">Replays they asked for</p><p className="qs">Clicked to watch, from email or SMS, by week</p>
                  <VBars labels={['Sep 7', 'Sep 14', 'Sep 21', 'Sep 28']} series={[{ color: C.terra, vals: [6, 8, 7, 9] }]} h={150} /></Card>
                <Card live><p className="q">Weekly Compass summary</p><p className="qs">Opened, by week · counted from Sept 28</p>
                  <VBars labels={em ? em.weeklySummary.map(w => w.label) : ['', '', '', '']} series={[{ color: C.forest2, vals: em ? em.weeklySummary.map(w => (w.sent ? w.opened : null)) : [null, null, null, null] }]} h={150} /></Card>
              </div>
              <div className="grid g3" style={{ marginTop: 16 }}>
                <Card live><p className="q">Quarterly check-ins</p><p className="qs">Done this quarter</p>
                  <div className="ring" style={{ background: `conic-gradient(var(--forest) 0 ${mem?.quarterlyCheckins.pct ?? 0}%,var(--sand) ${mem?.quarterlyCheckins.pct ?? 0}% 100%)` }}><div>{mem?.quarterlyCheckins.pct ?? 0}%</div></div>
                  <div className="lbl" style={{ textAlign: 'center' }}>{mem ? `${mem.quarterlyCheckins.active - mem.quarterlyCheckins.done} members still to do theirs this quarter` : '…'}</div>
                  <div className="row" style={{ justifyContent: 'center', marginTop: 10 }}><button type="button" className="btn p s">Send check-in nudges</button></div></Card>
                {/* Live Oct 8 2026: members who joined in the last 30 days. */}
                <Card live><p className="q">Onboarding</p><p className="qs">Members who joined in the last 30 days</p>
                  <Funnel steps={[['Joined', mem?.onboarding?.joined ?? null], ['Got the welcome email', mem?.onboarding?.welcomed ?? null], ['Came to a live event', mem?.onboarding?.live_event ?? null], ['Did a daily check-in', mem?.onboarding?.checked_in ?? null], ['In My Hub this week', mem?.onboarding?.active_week ?? null]]} /></Card>
                <Card live><p className="q">Most-used Hub pages</p><p className="qs">Visits this month · counted from Sept 29</p>
                  {mem?.hubSections?.length
                    ? <VBars labels={mem.hubSections.map(x => x.section)} series={[{ color: C.forest, vals: mem.hubSections.map(x => x.visits) }]} h={150} />
                    : <div className="empty">Counting started Sept 29. Sections show here as members use My Hub.</div>}</Card>
              </div>
              <Card live={false} style={{ marginTop: 16 }}><p className="q">Re-enrolment estimate</p><p className="qs">Members by how likely they are to renew, based on the last 30 days</p>
                <VBars labels={['Likely to renew', 'Unsure', 'At risk']} series={[{ color: C.forest, colors: [C.forest, C.amber, C.red], vals: [28, 6, 7] }]} h={150} /></Card>
              <div className="grid g3" style={{ marginTop: 16 }}>
                <Card live><p className="q">Why members leave</p><p className="qs">Reasons given when cancelling or archiving</p>
                  <VBars labels={['Price', 'No time', 'Not using it', 'Life change', 'Other']} series={[{ color: C.red, colors: [C.red, C.terra, C.terra, C.terra2, C.sand], vals: mem?.leaving?.reasons || [0, 0, 0, 0, 0] }]} max={Math.max(3, ...(mem?.leaving?.reasons || [0]))} h={150} />
                  {mem?.leaving?.reasonUnknown > 0 && <div className="note" style={{ marginTop: 10 }}>{mem.leaving.reasonUnknown} of those who left didn't give a reason we could record.</div>}</Card>
                <Card live><p className="q">What members ask for</p><p className="qs">From "Tell Us", this quarter · by the topic Team365 gives each request</p>
                  {mem?.asks?.length
                    ? <VBars labels={mem.asks.map(a => a.label)} series={[{ color: C.forest, vals: mem.asks.map(a => a.n) }]} max={Math.max(3, ...mem.asks.map(a => a.n))} h={150} />
                    : <div className="empty">No requests yet this quarter.</div>}
                  <div className="note" style={{ marginTop: 10 }}>Give a request a topic in Team365 → Requests (open it, then "Topic"). Until then it's grouped by its type.</div></Card>
                <Card live><p className="q">How long members stay</p><p className="qs">Active members by months in 365reset</p>
                  <VBars labels={['0–3 mo', '3–6 mo', '6–9 mo', '9+ mo']} series={[{ color: C.forest2, vals: mem ? mem.tenure : [0, 0, 0, 0] }]} h={150} /></Card>
              </div>
              {/* Live Oct 8 2026 — same Active rule as the Team365 Overview tile. */}
              <Card live style={{ marginTop: 16 }}><p className="q">Members who need a hand</p><p className="qs">Click a person to act · uses the Overview's Active rule · no sign of them in 7 days needs attention; in My Hub 4+ of the last 7 days is thriving</p>
                <div className="people">
                  {[['attention', 'Needs attention now'], ['sometimes', 'Sometimes in'], ['thriving', 'Thriving']].map(([k, label]) => {
                    const list = mem?.needAHand?.[k] || [];
                    return (
                      <div className="pcol" key={k}><h4>{label} <span>{mem ? list.length : '…'}</span></h4>
                        {list.map(p => (
                          <Link key={p.id} to={`/admin/members/${p.id}`} className="pchip" style={{ display: 'block' }}><b>{p.name}</b>{p.note}</Link>
                        ))}
                        {mem && list.length === 0 && <div className="pchip">Nobody right now</div>}
                      </div>
                    );
                  })}
                </div></Card>
            </section>
          )}

          {/* =============================== REFERRALS =============================== */}
          {page === 'referrals' && (
            <section>
              <h2 className="ptitle">Referrals</h2>
              <p className="pq">Everyone who sends people our way: members who simply share, and affiliates who earn. Thank them, reward them, and see it happen.</p>
              <div className="grid g4">
                <Card live><p className="q">Referred joins this month</p><div className="bign">{d ? d.referrals.thisMonth : '…'}</div><div className="lbl">Affiliates, plus names typed under "Who referred you?"</div></Card>
                <Card live><p className="q">From members</p><div className="bign">{d ? d.referrals.fromMembers : '…'}</div><div className="lbl">Not affiliates</div></Card>
                <Card live><p className="q">From affiliates</p><div className="bign">{d ? d.referrals.fromAffiliates : '…'}</div><div className="lbl">Detail in the Affiliate Control Room</div></Card>
                <Card live style={{ border: '2px solid var(--terra)' }}><p className="q">Thank-yous owed</p><div className="bign" style={{ color: 'var(--terra)' }}>{d ? d.referrals.thanks.owed : '…'}</div><div className="lbl">Open thank-you To-Dos · tick them off in To-Dos</div></Card>
              </div>
              <div className="grid g2" style={{ marginTop: 16 }}>
                <Card live><p className="q">Referrals per month</p><p className="qs">Members and affiliates, side by side</p>
                  {d && <VBars labels={d.referrals.byMonth.map(m => m.label)} series={[{ name: 'Members', color: C.forest, vals: d.referrals.byMonth.map(m => m.members) }, { name: 'Affiliates', color: C.terra2, vals: d.referrals.byMonth.map(m => m.affiliates) }]} />}</Card>
                <Card live><p className="q">Referral conversion</p><p className="qs">Everyone referred, this month · member referrals show at Joined</p>
                  <Funnel steps={referralSteps} /></Card>
              </div>
              <div className="grid g2" style={{ marginTop: 16 }}>
                <Card live><p className="q">Our member referrers</p><p className="qs">Joins they brought in, all time</p>
                  {d && (d.referrals.memberReferrers.length === 0
                    ? <div className="empty">No member referrals yet. When someone joins and types a member's name under "Who referred you?", that member shows up here.</div>
                    : <VBars labels={d.referrals.memberReferrers.map(r => r.name)} series={[{ color: C.forest, vals: d.referrals.memberReferrers.map(r => r.joins) }]} max={Math.max(3, ...d.referrals.memberReferrers.map(r => r.joins))} />)}</Card>
                <Card live={false}><p className="q">When do members refer?</p><p className="qs">What happened in the week before a member referred someone</p>
                  <VBars labels={['After a live event', 'After a win in Compass', 'After a lesson', 'Asked by Jenn']} series={[{ color: C.terra, vals: [3, 1, 0, 2] }]} />
                  <div className="note"><b>Why:</b> knowing the moment tells us when to ask. For example, right after a great live event.</div></Card>
              </div>
              <Card live={false} style={{ marginTop: 16 }}><p className="q">Thank and reward</p><p className="qs">Every member referral gets a thank-you. Tick it off here.</p>
                <div className="people">
                  <div className="pcol"><h4>To thank <span>2</span></h4><div className="pchip"><b>Example member</b>Referred a friend who joined Sept 30</div><div className="pchip"><b>Example member</b>Referred a friend who joined Oct 2</div></div>
                  <div className="pcol"><h4>Thanked <span>1</span></h4><div className="pchip"><b>Example member</b>Personal note sent</div></div>
                  <div className="pcol"><h4>Rewarded <span>1</span></h4><div className="pchip"><b>Example member</b>Reward chosen</div></div>
                </div>
                <div className="row" style={{ marginTop: 12 }}><button type="button" className="btn p">Send a thank-you note</button><button type="button" className="btn">Choose a reward</button><button type="button" className="btn">Thank them in My Hive</button></div></Card>
              <div className="grid g2" style={{ marginTop: 16 }}>
                <Card live={false}><p className="q">Invite members to share</p><p className="qs">Tools for members who'd never be affiliates</p>
                  <div className="row"><button type="button" className="btn p">"Bring a friend" message for members</button><button type="button" className="btn">Shareable invite card (Canva)</button><button type="button" className="btn">Ask at the end of a live event</button></div>
                  <div className="note"><b>Easy for them:</b> the friend only needs to type the member's name under "Who referred you?" at checkout. No links or codes needed.</div></Card>
                <Card live={false}><p className="q">Reward ideas</p><p className="qs">Our workspace for choosing rewards</p>
                  <div className="grid g2">
                    <div className="sticky">A handwritten card from Cape Breton</div><div className="sticky f">A month free</div>
                    <div className="sticky t">A shout-out in My Hive</div><div className="sticky">First pick of seats at a special event</div>
                  </div></Card>
              </div>
              {isAffiliateManager && <div className="note">Affiliate payouts, landing pages and pipelines stay in the Affiliate Control Room. <Link to="/admin/affiliate-manager" className="btn s" style={{ marginLeft: 6 }}>Open Affiliate Control Room →</Link></div>}
            </section>
          )}

          {/* =============================== VISIBILITY =============================== */}
          {page === 'visibility' && (
            <section>
              <h2 className="ptitle">Visibility</h2>
              <p className="pq">How visible are we? Who wants to know more? Will they buy, how fast, and in how many steps?</p>
              <div className="grid g2">
                <Card live><p className="q">Are people wanting to find out more?</p><p className="qs">Visits to 365reset.com by week</p>
                  {d && <VBars labels={d.visitsByWeek.map(w => w.label)} series={[{ color: C.terra, vals: d.visitsByWeek.map(w => w.n) }]} />}</Card>
                {/* Live Oct 8 2026 (Jenn: get the In-development tracking numbers done) — same real visitor
                    data as the strangers card; email sign-up and pre-signup engagement aren't measured yet. */}
                <Card live><p className="q">Levels of interest</p><p className="qs">This month</p>
                  <Funnel steps={strangerSteps} /></Card>
              </div>
              <div className="grid g2" style={{ marginTop: 16 }}>
                <Card live><p className="q">What do they like?</p><p className="qs">Top posts by visits</p>
                  {d && (topPosts.length === 0
                    ? <div className="empty">No tagged posts yet. Each post shows up here as soon as someone clicks its tagged link.</div>
                    : <VBars labels={topPosts.map(p => `${p.post} · ${p.joins} ${p.joins === 1 ? 'join' : 'joins'}`)} series={[{ color: C.terra2, colors: topPosts.map(p => (p.joins > 0 ? C.forest : C.terra2)), vals: topPosts.map(p => p.visits) }]} keyItems={[['Brought a join', C.forest], ['Visits only', C.terra2]]} />)}
                  <div className="row" style={{ marginTop: 10 }}><button type="button" className="btn k s" disabled={!d?.posts?.length} onClick={() => downloadPostsCsv(d.posts)}>Download as spreadsheet</button></div></Card>
                <Card live><p className="q">Who are they?</p><p className="qs">New members' answer to "How did you hear about us?"</p>
                  {d && <VBars labels={heard.map(h => h.answer)} series={[{ color: C.forest, vals: heard.map(h => h.n) }]} max={Math.max(3, ...heard.map(h => h.n))} />}
                  <div className="note"><b>Privacy:</b> visitors stay anonymous. We only learn who someone is when they give their name.</div></Card>
              </div>
              <div className="grid g3" style={{ marginTop: 16 }}>
                <Card live><p className="q">Are early adopters buying?</p><p className="qs">New members by week</p>
                  {d && <VBars labels={d.joinsByWeek.map(w => w.label)} series={[{ color: C.forest, vals: d.joinsByWeek.map(w => w.n) }]} />}</Card>
                <Card live><p className="q">How long to convert?</p><p className="qs">Days from first visit to joining · new members since Sept 28</p>
                  <VBars labels={['Same day', '1–7 days', '8–30 days', '30+ days']} series={[{ color: C.terra, vals: d?.conversion?.daysToJoin || [0, 0, 0, 0] }]} max={Math.max(2, ...(d?.conversion?.daysToJoin || [0]))} /></Card>
                <Card live><p className="q">How many steps?</p><p className="qs">Joins by path · new members since Sept 28</p>
                  <VBars labels={['Post → join', 'Event → email → join', 'Referral → join']} series={[{ color: C.forest, vals: d?.conversion?.paths || [0, null, 0] }]} max={Math.max(3, ...((d?.conversion?.paths || []).filter(v => v != null)))} />
                  {notYet('the event → email path, until emails and live events are tracked')}</Card>
              </div>

              <h2 className="ptitle">Platform by platform</h2>
              <p className="pq">Which platforms work best, for 365reset and for Jenn.</p>
              <Card live><p className="q">Visits by platform</p><p className="qs">This month · 365reset accounts and Jenn's accounts</p>
                {d && <VBars labels={d.visitsByPlatform.map(p => p.platform)} series={[{ name: '365reset accounts', color: C.terra, vals: d.visitsByPlatform.map(p => p.brand) }, { name: "Jenn's accounts", color: C.forest, vals: d.visitsByPlatform.map(p => p.jenn) }]} h={190} />}</Card>
              {isAffiliateManager && <PersonalFacebookViews Card={Card} />}
              <div className="grid g2" style={{ marginTop: 16 }}>
                <Card live><p className="q">Joins by platform</p><p className="qs">This month</p>
                  {d && <VBars labels={d.joinsByPlatform.map(p => p.platform)} series={[{ color: C.forest, vals: d.joinsByPlatform.map(p => p.n) }]} max={Math.max(3, ...d.joinsByPlatform.map(p => p.n))} />}</Card>
                <Card live={false}><p className="q">Where our people hang out</p><p className="qs">Where prospects and members spend time, from check-in answers and research</p>
                  <VBars labels={['Facebook groups', 'Instagram', 'Substack', 'Pinterest', 'YouTube', 'TikTok']} series={[{ color: C.terra2, vals: [40, 28, 14, 12, 10, 6] }]} fmt={v => `${v}%`} /></Card>
              </div>
              <Card live={false} style={{ marginTop: 16 }}><p className="q">Stay visible to prospects</p><p className="qs">People, groups and hashtags to follow and engage with, and when we last showed up there</p>
                <div className="people">
                  <div className="pcol"><h4>Engage today <span>4</span></h4><div className="pchip"><b>Example Facebook group</b>Women in business, Nova Scotia · last seen 6 days ago</div><div className="pchip"><b>Example Substack writer</b>Burnout recovery · comment on the new post</div></div>
                  <div className="pcol"><h4>This week <span>6</span></h4><div className="pchip"><b>#examplehashtag</b>Instagram · 3 posts to join</div></div>
                  <div className="pcol"><h4>Up to date <span>9</span></h4><div className="pchip"><b>Example LinkedIn creator</b>Engaged yesterday</div></div>
                </div>
                <div className="row" style={{ marginTop: 12 }}><button type="button" className="btn p">+ Add someone to follow</button><button type="button" className="btn">Claude suggests who to follow</button></div></Card>

              <h2 className="ptitle">Reputation</h2>
              <p className="pq">What's being said about 365reset, about Jenn, and about our industry. How we measure this is still to be decided.</p>
              <div className="grid g3">
                <Card live={false}><p className="q">Mentions of us</p><p className="qs">By week</p>
                  <VBars labels={['Sep 7', 'Sep 14', 'Sep 21', 'Sep 28']} series={[{ color: C.terra2, vals: [1, 0, 2, 3] }]} h={140} /></Card>
                <Card live={false}><p className="q">How people feel</p><p className="qs">Mentions this month</p>
                  <VBars labels={['Positive', 'Neutral', 'Negative']} series={[{ color: C.forest, colors: [C.forest, C.sand, C.red], vals: [4, 2, 0] }]} h={140} /></Card>
                <Card live={false}><p className="q">Reviews</p><p className="qs">Google, Facebook and others</p>
                  <div className="bign">— <small>stars</small></div><div className="lbl">Average rating and count show here</div>
                  <div className="row" style={{ marginTop: 12 }}><button type="button" className="btn p s">Ask happy members for a review</button></div></Card>
              </div>
              <Card live={false} style={{ marginTop: 16 }}><p className="q">Alerts</p><p className="qs">Anything that could take away from our reputation, about us or about our industry. The top one also shows on the dashboard.</p>
                <div className="quiet" style={{ marginTop: 0 }}><div><b>Example: industry article</b><span>"Are online wellness memberships worth it?" · Canada · Sept 27 · <u>Plan a reply</u></span></div></div></Card>
            </section>
          )}

          {/* =============================== INTERESTED PEOPLE =============================== */}
          {page === 'leads' && <InterestedPeoplePage />}

          {/* =============================== LIVE EVENTS =============================== */}
          {page === 'events' && (
            <section>
              <h2 className="ptitle">LM Live Events</h2>
              <p className="pq">Lead magnet live events: the free events that bring new people in. Separate from the live events members attend in My Hub.</p>
              <MkInitiatives kind="live_event" />
              <div className="grid g2">
                <Card live={false}><p className="q">Last event: Sleep workshop</p><p className="qs">Registered to joined</p>
                  <Funnel steps={[['Registered', 40], ['Attended live', 18], ['Stayed to the invitation', 12], ['Joined', 3]]} /></Card>
                <Card live={false}><p className="q">Which approach works best?</p><p className="qs">Percent of registrants who joined</p>
                  <VBars labels={['Sleep · Tue 2 pm', 'Morning · Sat 10 am', 'Craft · Thu 7 pm']} series={[{ color: C.forest, vals: [8, 4, 6] }]} fmt={v => `${v}%`} max={10} />
                  <div className="note"><b>Real benchmark:</b> 44–50% of registrants attend live, and Tuesday at 2 pm scored best (Contrast, 1M+ registrants, 2026).</div></Card>
              </div>
              <Card live={false} style={{ marginTop: 16 }}><p className="q">Event builder</p><p className="qs">Where we design the next event together</p>
                <div className="ws">
                  <label className="fld">Event name<input defaultValue="Example: The Ten-Minute Morning" /></label>
                  <label className="fld">Date and time<input defaultValue="Tuesday · 2:00 pm Atlantic" /></label>
                  <label className="fld">The promise<textarea defaultValue="Example: one ten-minute practice you'll actually keep" /></label>
                  <label className="fld">The invitation at the end<textarea defaultValue="Example: join this week and your first live session is Thursday" /></label>
                </div>
                <div className="row" style={{ marginTop: 14 }}>{['Registration page', 'Zoom link', 'Reminder emails', 'Tagged promo links', 'Replay + follow-up'].map((b, i) => <button type="button" key={b} className={`btn ${i === 0 ? 'p' : ''}`}>{b}</button>)}</div></Card>
              <Card live={false} style={{ marginTop: 16 }}><p className="q">Promo posts for this event</p><p className="qs">The social posts that fill the event. Each one carries its own tagged link, so we see which post brought each registration.</p>
                <div className="qs" style={{ margin: 0 }}>Registrations by promo post</div>
                <VBars labels={['Facebook post', 'Instagram post', 'Email', 'Jenn · LinkedIn', 'Affiliates']} series={[{ color: C.terra, vals: [14, 8, 11, 4, 3] }]} h={150} />
                <div className="row" style={{ marginTop: 12 }}><button type="button" className="btn p">Build the promo posts</button><button type="button" className="btn">Get the tagged links</button></div></Card>
            </section>
          )}

          {/* =============================== LEAD MAGNETS =============================== */}
          {page === 'magnets' && (
            <section>
              <h2 className="ptitle">Lead magnets</h2>
              <p className="pq">Free, always-on ways in, plus this quarter's lead magnet campaign.</p>
              <MkInitiatives kind="lead_magnet" />
              <div className="grid g3">
                {[['365reset quiz', [['Started', 80], ['Gave email', 30], ['Joined', 2]]], ['5-day Sleep Reset', [['Signed up', 25], ['Finished day 5', 11], ['Joined', 2]]], ['Workshop replay', [['Opened', 40], ['Watched most', 15], ['Joined', 1]]]].map(([name, steps]) => (
                  <Card key={name} live={false}><p className="q">{name}</p><p className="qs">Example</p><Funnel steps={steps} /></Card>
                ))}
              </div>
              <Card live={false} style={{ marginTop: 16 }}><p className="q">Which lead magnet converts best?</p><p className="qs">Joins per 100 sign-ups</p>
                <VBars labels={['Sleep Reset', 'Replay', 'Quiz']} series={[{ color: C.forest, vals: [8, 3, 2.5] }]} max={10} /></Card>
            </section>
          )}

          {/* =============================== MINI-LAB =============================== */}
          {page === 'lab' && (
            <section>
              <h2 className="ptitle">Mini-lab</h2>
              <p className="pq">Every tactic we're trying, week by week. Shifts and stalls show what works and when to post.</p>
              <LabNeedle lab={lab} />
              <div className="grid g2" style={{ marginTop: 16 }}>
                <Card live><p className="q">Best day to post</p><p className="qs">Visits and joins by day of the week, this month</p>
                  <VBars labels={DAYS} series={[{ color: C.terra2, colors: byDayVals.map(x => (x.joined ? C.forest : C.terra2)), vals: byDayVals.map(x => x.visits) }]} keyItems={byDayKeys} /></Card>
                <Card live><p className="q">Best time to post</p><p className="qs">Visits by time of day (Atlantic), this month</p>
                  <VBars labels={HOURS} series={[{ color: C.terra, vals: d?.byHour || HOURS.map(() => 0) }]} /></Card>
              </div>
              <LabExperiments lab={lab} reload={loadLab} />
              <h2 className="ptitle">Campaign kits</h2>
              <p className="pq">Every post we've made, week by week, with its pictures and how it did. Reuse the good ones as evergreen posts.</p>
              <LabKits lab={lab} />
              <Card live={false} style={{ marginTop: 16, border: '2px dashed var(--sand-2)' }}><p className="q">Post from here</p><p className="qs">For later, once 365reset is earning: schedule and publish every post straight from this system, with its tagged link added automatically</p>
                <VBars labels={['Facebook', 'Instagram', 'Threads', 'TikTok', 'YouTube', 'Pinterest', 'LinkedIn', 'Bluesky']} series={[{ color: C.sand, vals: [7, 7, 7, 3, 3, 7, 7, 7] }]} h={110} fmt={v => `${v} posts`} />
                <div className="row" style={{ marginTop: 12 }}><button type="button" className="btn p">Schedule the week</button><button type="button" className="btn">Connect accounts</button></div></Card>
            </section>
          )}

          {/* =============================== MARKET PULSE =============================== */}
          {page === 'contests' && <ContestsPage />}
          {page === 'posts' && <WeekPostsPage />}
          {/* Jenn, 2026-10-20: reviewed Thursdays at the work-along, scheduled Fridays — always a week ahead. */}
          {page === 'nextposts' && <WeekPostsPage key="next" which="next" />}
          {page === 'brand' && <BrandVisionPage />}
          {page === 'mylinks' && <MyLinksPage />}

          {page === 'market' && <MarketPulsePage />}

          {/* =============================== PROOF =============================== */}
          {page === 'proof' && <ProofPage />}

          {/* =============================== INVENTORY =============================== */}
          {page === 'inventory' && <PostGridPage />}

          {/* =============================== TOOLS =============================== */}
          {page === 'tools' && (
            <section>
              <h2 className="ptitle">Tools</h2>
              <p className="pq">Everything we reach for, one click away.</p>
              <div className="grid g2">
                <Card live><p className="q">Tagged link builder</p><p className="qs">Follows CB's link rules automatically</p><LinkBuilder /></Card>
                <div className="card">
                  <p className="q">Open in one click</p>
                  <div className="row" style={{ marginTop: 12 }}>
                    {[['Canva Content Planner', 'https://www.canva.com/'], ['Google Analytics', 'https://analytics.google.com/'], ['Metricool', 'https://app.metricool.com/'],
                      // Always this week's Metricool file (Jenn, 2026-10-12): Claude replaces
                      // this-week.csv on the server each week (copy also in that week's Drive folder).
                      // Oct 19 (Jenn): the CSV's words column was unreadable on iPad/phone. Read and
                      // edit the words on This week's posts (saves into this-week.csv); CSV kept for import.
                      ["This week's posts — read and edit", '/admin/sales-marketing#posts'],
                      ['Metricool import file (CSV)', 'https://365reset.ca/uploads/social/this-week.csv'], ['365reset.com', 'https://365reset.com/']]
                      .map(([label, href]) => <a key={label} className="btn" href={href} target="_blank" rel="noopener noreferrer">{label}</a>)}
                  </div>
                </div>
              </div>
              <div className="card" style={{ marginTop: 16 }}><IdeasList /></div>
            </section>
          )}

          <footer>Built from CB's design, v4 · Sept 28, 2026. Live cards show real numbers; cards marked "In development" show CB's example numbers.</footer>
        </div>
      </div>
    </DashboardLayout>
  );
}

// ─── Work timer (Jenn, 2026-09-29: "we're making our system the place to
// actually do the work. Can we set up a timer for that?") ─────────────────────
// Always at the top of Sales & Marketing. Kept on the server, so closing the
// page or switching to the phone never loses a running timer.
const fmtClock = (ms) => {
  const t = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(t / 3600); const m = Math.floor((t % 3600) / 60); const sec = t % 60;
  return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
};

function TimerBar({ planData, onChange, boxed = false }) {
  const { timer, campaigns, serverNow } = planData;
  const [campaign, setCampaign] = useState(campaigns[0]);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now());
  // Server and device clocks can differ — measure from the server's clock.
  const skew = serverNow ? new Date(serverNow).getTime() - Date.now() : 0;
  useEffect(() => {
    if (!timer) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [timer]);
  const act = async (path, body) => {
    setBusy(true);
    try { await api.post(`/admin/sales-marketing/timer/${path}`, body || {}); onChange(); } catch { /* shown by reload */ } finally { setBusy(false); }
  };
  // Boxed (Dashboard, beside "This week's focus"): stacked top to bottom, bigger.
  if (boxed) {
    return (
      <div className="card">
        <p className="q">⏱ Time your work</p>
        <p className="qs">Pick what you're working on, then Start</p>
        {timer ? (
          <>
            <p style={{ fontSize: 15, margin: '0 0 4px' }}>Timing <b>{timer.campaign}</b></p>
            <p className="bign" style={{ fontVariantNumeric: 'tabular-nums', margin: '4px 0 14px' }}>{fmtClock(now + skew - new Date(timer.started_at).getTime())}</p>
            <button type="button" className="btn p" style={{ minHeight: 44, padding: '10px 24px' }} disabled={busy} onClick={() => act('stop')}>Stop</button>
          </>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-start' }}>
            <select value={campaign} onChange={e => setCampaign(e.target.value)} className="btn" style={{ minHeight: 44, fontSize: 15 }} aria-label="What you're working on">
              {campaigns.map(c => <option key={c}>{c}</option>)}
            </select>
            <button type="button" className="btn p" style={{ minHeight: 44, padding: '10px 24px' }} disabled={busy} onClick={() => act('start', { campaign })}>Start</button>
          </div>
        )}
      </div>
    );
  }
  return (
    <div className="card" style={{ marginTop: 14, padding: '12px 16px' }}>
      {timer ? (
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <span style={{ fontSize: 15 }}>⏱ Timing <b>{timer.campaign}</b> · <b style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtClock(now + skew - new Date(timer.started_at).getTime())}</b></span>
          <button type="button" className="btn p s" disabled={busy} onClick={() => act('stop')}>Stop</button>
        </div>
      ) : (
        <div className="row">
          <span style={{ fontSize: 14, fontWeight: 600 }}>⏱ Time your work:</span>
          <select value={campaign} onChange={e => setCampaign(e.target.value)} className="btn s" style={{ paddingRight: 8 }} aria-label="What you're working on">
            {campaigns.map(c => <option key={c}>{c}</option>)}
          </select>
          <button type="button" className="btn p s" disabled={busy} onClick={() => act('start', { campaign })}>Start</button>
        </div>
      )}
    </div>
  );
}

// ─── Goals & plan (To-Do 10, 2026-09-29) — CB's layout, filled with what Jenn sets.
const RESULT_LABELS = { measuring: 'Measuring', worked: 'Worked', didnt_work: "Didn't work", done: 'Done' };
const RESULT_TAG = { measuring: 'a', worked: 'g', didnt_work: 'r', done: 'g' };
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function Suggested({ show }) {
  return show ? <p className="qs" style={{ margin: '0 0 10px', color: '#7A5210' }}>CB's suggestion — set your own.</p> : null;
}

function GoalsPage({ planData, reload, d }) {
  const [editingGoals, setEditingGoals] = useState(false);
  const [editingStrategy, setEditingStrategy] = useState(false);
  const [addingTime, setAddingTime] = useState(false);
  const [addingCost, setAddingCost] = useState(false);
  const [addingDecision, setAddingDecision] = useState(false);
  const [msg, setMsg] = useState(null);
  if (!planData) return <p className="pq" style={{ marginTop: 30 }}>Loading your plan…</p>;
  const { plan, campaigns, hours, perCampaign, costs, decisions } = planData;
  const L = plan.goal_ladder; const G = plan.campaign_goals; const S = plan.strategy;
  const now = new Date();
  const q = Math.floor(now.getMonth() / 3) + 1;
  const save = async (fn) => { setMsg(null); try { await fn(); reload(); return true; } catch (e) { setMsg(e.response?.data?.error || 'Could not save.'); return false; } };
  const mem = d?.members;
  const soFar = [d?.everydayPosts?.joins ?? 0, d?.affiliatesThisQuarter ?? 0, null, mem?.weeklyActive.pct ?? 0, null];

  return (
    <section>
      <h2 className="ptitle">Goals &amp; plan</h2>
      <p className="pq">Where we set the goals, choose the strategy, and decide each week. Everything on the dashboard measures against what's set here.</p>
      {msg && <div className="note">{msg}</div>}

      <Card live>
        <p className="q">The goal ladder</p><p className="qs">Year → quarter → month → week → today. Set once, then each level feeds the next.</p>
        <Suggested show={!L.confirmed} />
        <div className="ladder">
          <div className="rung"><span className="count">Break-even</span><div className="bign">{L.breakEven}</div><div className="lbl">monthly paying members (break-even)</div></div>
          <div className="rung"><span className="count">Q{q} · quarter</span><div className="bign">{L.quarter.target}</div><div className="lbl">{L.quarter.note}</div></div>
          <div className="rung"><span className="count">{MONTHS[now.getMonth()]} · month</span><div className="bign">{L.month.target}</div><div className="lbl">{L.month.note}</div></div>
          <div className="rung"><span className="count">This week</span><div className="bign">{L.week.target}</div><div className="lbl">{L.week.note}</div></div>
          <div className="rung" style={{ border: '2px solid var(--terra)' }}><span className="count">Today</span><div className="bign">{L.today.target}</div><div className="lbl">{L.today.note}</div></div>
        </div>
        <div className="row" style={{ marginTop: 14 }}>
          <button type="button" className="btn p" onClick={() => setEditingGoals(v => !v)}>{editingGoals ? 'Close' : 'Set or change goals'}</button>
        </div>
        {editingGoals && <GoalsForm plan={plan} onDone={() => setEditingGoals(false)} save={save} />}
        <MovesLog target={L.today.target} />
      </Card>

      <div className="grid g2" style={{ marginTop: 16 }}>
        <Card live><p className="q">Campaign goals this quarter</p><p className="qs">Each campaign's target, set here, shown as its thermometer on the dashboard</p>
          <Suggested show={!G.confirmed} />
          <VBars labels={['Everyday posts', 'Affiliates', 'Lead magnet', 'Sticky (%)', 'December renewals']}
            series={[{ name: 'Goal', color: C.sand, vals: [G.everydayPosts, G.affiliates, G.leadMagnet, G.stickyPct, G.decemberRenewals] }, { name: 'So far', color: C.forest, vals: soFar }]} />
          <div className="note" style={{ marginTop: 10 }}>Lead magnet and December renewals show "—" until those campaigns start.</div></Card>
        <Card live><p className="q">Where our time goes</p><p className="qs">Hours this week by campaign, from the timer · the number beside each name is joins per hour this quarter</p>
          <VBars labels={perCampaign.map(p => `${p.campaign} · ${p.joinsPerHour == null ? '—' : p.joinsPerHour}`)} series={[{ color: C.terra, vals: hours.thisWeek }]} fmt={v => `${v} h`} />
          <div className="note"><b>Why:</b> with 1–2 hours a day, the best campaign is the one that brings the most members per hour. Use the ⏱ timer at the top while you work.</div>
          <div className="row" style={{ marginTop: 10 }}><button type="button" className="btn s" onClick={() => setAddingTime(v => !v)}>{addingTime ? 'Close' : 'Forgot the timer? Add time'}</button></div>
          {addingTime && <AddTimeForm campaigns={campaigns} save={save} onDone={() => setAddingTime(false)} />}</Card>
      </div>

      <div className="grid g2" style={{ marginTop: 16 }}>
        <Card live><p className="q">What each new member costs</p><p className="qs">Money spent per new member, by campaign, this quarter (CA$)</p>
          <VBars labels={perCampaign.map(p => p.campaign)} series={[{ color: C.terra2, vals: perCampaign.map(p => (p.joins == null ? null : (p.costPerMember ?? 0))) }]} fmt={v => `$${v}`} />
          {costs.length > 0 && (
            <div className="tw" style={{ marginTop: 10 }}><table>
              <thead><tr><th>Date</th><th>Campaign</th><th className="n">CA$</th><th>Note</th><th /></tr></thead>
              <tbody>{costs.map(c => (
                <tr key={c.id}><td>{c.spent_on}</td><td>{c.campaign}</td><td className="n">{c.amount.toFixed(2)}</td><td>{c.note || ''}</td>
                  <td><button type="button" className="btn s" onClick={() => { if (window.confirm('Remove this cost?')) save(() => api.delete(`/admin/sales-marketing/costs/${c.id}`)); }}>Remove</button></td></tr>
              ))}</tbody>
            </table></div>
          )}
          <div className="row" style={{ marginTop: 10 }}><button type="button" className="btn s" onClick={() => setAddingCost(v => !v)}>{addingCost ? 'Close' : '+ Add a cost'}</button></div>
          {addingCost && <AddCostForm campaigns={campaigns} save={save} onDone={() => setAddingCost(false)} />}</Card>
        <Card live><p className="q">Strategy on one page</p><p className="qs">The plan for this quarter, in our words</p>
          <Suggested show={!S.confirmed} />
          {editingStrategy ? <StrategyForm strategy={S} save={save} onDone={() => setEditingStrategy(false)} /> : (
            <>
              <div className="grid g2">
                <Sticky tone="f" title="Who we're reaching">{S.who}</Sticky>
                <Sticky title="Our main message">{S.message}</Sticky>
                <Sticky tone="t" title="Top 3 tactics">{S.tactics}</Sticky>
                <Sticky title="What we're not doing">{S.notDoing}</Sticky>
              </div>
              <div className="row" style={{ marginTop: 10 }}><button type="button" className="btn s" onClick={() => setEditingStrategy(true)}>Edit strategy</button></div>
            </>
          )}</Card>
      </div>

      <h2 className="ptitle">Our rhythm</h2>
      <p className="pq">The same simple routine every time, so nothing depends on memory.</p>
      <div className="grid g4">
        <Card live={false}><p className="q">Daily · 15 min</p><p className="qs">Reply to interested people, check alerts, post today's post</p><button type="button" className="btn s">Open today's list</button></Card>
        <Card live={false}><p className="q">Weekly · Monday</p><p className="qs">Last week's numbers → decisions → this week's posts and links</p><button type="button" className="btn s">Start weekly review</button></Card>
        <Card live={false}><p className="q">Monthly</p><p className="qs">Campaign results, time and cost per member, Market pulse</p><button type="button" className="btn s">Start monthly review</button></Card>
        <Card live={false}><p className="q">Quarterly &amp; yearly</p><p className="qs">Reset the goal ladder and choose next quarter's lead magnet campaign</p><button type="button" className="btn s">Start quarterly plan</button></Card>
      </div>

      <div className="grid g2" style={{ marginTop: 16 }}>
        <Card live><p className="q">Decision log</p><p className="qs">What we decided, why, and what happened. It stops us repeating what didn't work.</p>
          <div className="tw"><table>
            <thead><tr><th>Date</th><th>Decision</th><th>Result</th></tr></thead>
            <tbody>{decisions.map(x => (
              <tr key={x.id}>
                <td>{x.label}</td>
                <td>{x.decision}{x.why && <div className="qs" style={{ margin: 0 }}>Why: {x.why}</div>}</td>
                <td>
                  <select value={x.result} aria-label="Result" className={`tag ${RESULT_TAG[x.result]}`} style={{ border: 0 }}
                    onChange={e => save(() => api.put(`/admin/sales-marketing/decisions/${x.id}`, { result: e.target.value }))}>
                    {Object.entries(RESULT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </td>
              </tr>
            ))}</tbody>
          </table></div>
          <div className="row" style={{ marginTop: 10 }}><button type="button" className="btn s" onClick={() => setAddingDecision(v => !v)}>{addingDecision ? 'Close' : '+ Log a decision'}</button></div>
          {addingDecision && <DecisionForm save={save} onDone={() => setAddingDecision(false)} />}</Card>
        <Card live style={{ border: '2px solid var(--forest)' }}><p className="q">Claude's data pack</p><p className="qs">What Claude needs to plan and act with you, each Monday</p>
          <div className="row">{["Last week's numbers", 'Goals', 'Costs', "Jenn's hours", 'Decision log', 'Upcoming events', 'New testimonials'].map(t => <span key={t} className="tag g">{t}</span>)}</div>
          <div className="row" style={{ marginTop: 12 }}><button type="button" className="btn p" onClick={() => downloadDataPack(d, planData)}>Download this week's data pack</button></div>
          <div className="note">One spreadsheet with everything above. Attach it in your chat with CB each Monday. (Claude Code can already see these numbers directly.)</div></Card>
      </div>
    </section>
  );
}

const Field = ({ label, children }) => <label className="fld">{label}{children}</label>;

function GoalsForm({ plan, save, onDone }) {
  const [L, setL] = useState(JSON.parse(JSON.stringify(plan.goal_ladder)));
  const [G, setG] = useState({ ...plan.campaign_goals });
  const [F, setF] = useState([...(plan.week_focus.items || []), '', '', ''].slice(0, 3));
  const lvl = (k, f, v) => setL(x => ({ ...x, [k]: { ...x[k], [f]: v } }));
  const submit = async () => {
    const ok = await save(async () => {
      await api.put('/admin/sales-marketing/plan/goal_ladder', L);
      await api.put('/admin/sales-marketing/plan/campaign_goals', G);
      await api.put('/admin/sales-marketing/plan/week_focus', { items: F });
    });
    if (ok) onDone();
  };
  return (
    <div style={{ marginTop: 14 }}>
      <p className="q" style={{ fontSize: 15 }}>The goal ladder</p>
      <div className="ws">
        <Field label="Break-even (monthly paying members)"><input type="number" value={L.breakEven} onChange={e => setL(x => ({ ...x, breakEven: e.target.value }))} /></Field>
        {[['quarter', 'This quarter'], ['month', 'This month'], ['week', 'This week'], ['today', 'Today']].map(([k, label]) => (
          <Field key={k} label={label}>
            <input type="number" value={L[k].target} onChange={e => lvl(k, 'target', e.target.value)} />
            <input value={L[k].note} onChange={e => lvl(k, 'note', e.target.value)} placeholder="What it counts" />
          </Field>
        ))}
      </div>
      <p className="q" style={{ fontSize: 15, marginTop: 16 }}>Campaign goals this quarter</p>
      <div className="ws">
        {[['everydayPosts', 'Everyday posts (new members)'], ['affiliates', 'Affiliates (joins)'], ['leadMagnet', 'Lead magnet (joins)'], ['stickyPct', 'Sticky members (% active weekly)'], ['decemberRenewals', 'December renewals']].map(([k, label]) => (
          <Field key={k} label={label}><input type="number" value={G[k]} onChange={e => setG(x => ({ ...x, [k]: e.target.value }))} /></Field>
        ))}
      </div>
      <p className="q" style={{ fontSize: 15, marginTop: 16 }}>This week's focus (up to 3, shown on the dashboard)</p>
      <div className="ws">
        {F.map((t, i) => <Field key={i} label={`${i + 1}.`}><input value={t} onChange={e => setF(x => x.map((y, j) => (j === i ? e.target.value : y)))} /></Field>)}
      </div>
      <div className="row" style={{ marginTop: 12 }}><button type="button" className="btn p" onClick={submit}>Save goals</button><button type="button" className="btn" onClick={onDone}>Cancel</button></div>
    </div>
  );
}

function StrategyForm({ strategy, save, onDone }) {
  const [S, setS] = useState({ who: strategy.who, message: strategy.message, tactics: strategy.tactics, notDoing: strategy.notDoing });
  const submit = async () => { if (await save(() => api.put('/admin/sales-marketing/plan/strategy', S))) onDone(); };
  return (
    <div>
      <div className="ws">
        {[['who', "Who we're reaching"], ['message', 'Our main message'], ['tactics', 'Top 3 tactics'], ['notDoing', "What we're not doing"]].map(([k, label]) => (
          <Field key={k} label={label}><textarea value={S[k]} onChange={e => setS(x => ({ ...x, [k]: e.target.value }))} /></Field>
        ))}
      </div>
      <div className="row" style={{ marginTop: 10 }}><button type="button" className="btn p s" onClick={submit}>Save strategy</button><button type="button" className="btn s" onClick={onDone}>Cancel</button></div>
    </div>
  );
}

function AddTimeForm({ campaigns, save, onDone }) {
  const [campaign, setCampaign] = useState(campaigns[0]);
  const [minutes, setMinutes] = useState('');
  const submit = async () => { if (await save(() => api.post('/admin/sales-marketing/timer/add', { campaign, minutes }))) onDone(); };
  return (
    <div className="ws" style={{ marginTop: 10 }}>
      <Field label="What you worked on"><select value={campaign} onChange={e => setCampaign(e.target.value)}>{campaigns.map(c => <option key={c}>{c}</option>)}</select></Field>
      <Field label="Minutes"><input type="number" min="1" value={minutes} onChange={e => setMinutes(e.target.value)} placeholder="e.g. 30" /></Field>
      <div className="row"><button type="button" className="btn p s" onClick={submit}>Add time</button></div>
    </div>
  );
}

function AddCostForm({ campaigns, save, onDone }) {
  const [c, setC] = useState({ campaign: campaigns[0], amount: '', note: '', spentOn: new Date().toISOString().slice(0, 10) });
  const submit = async () => { if (await save(() => api.post('/admin/sales-marketing/costs', c))) onDone(); };
  return (
    <div className="ws" style={{ marginTop: 10 }}>
      <Field label="Campaign"><select value={c.campaign} onChange={e => setC(x => ({ ...x, campaign: e.target.value }))}>{campaigns.map(x => <option key={x}>{x}</option>)}</select></Field>
      <Field label="Amount (CA$)"><input type="number" min="0" step="0.01" value={c.amount} onChange={e => setC(x => ({ ...x, amount: e.target.value }))} /></Field>
      <Field label="Date"><input type="date" value={c.spentOn} onChange={e => setC(x => ({ ...x, spentOn: e.target.value }))} /></Field>
      <Field label="What it was for (optional)"><input value={c.note} onChange={e => setC(x => ({ ...x, note: e.target.value }))} /></Field>
      <div className="row"><button type="button" className="btn p s" onClick={submit}>Add cost</button></div>
    </div>
  );
}

function DecisionForm({ save, onDone }) {
  const [x, setX] = useState({ decision: '', why: '', decidedOn: new Date().toISOString().slice(0, 10) });
  const submit = async () => { if (await save(() => api.post('/admin/sales-marketing/decisions', x))) onDone(); };
  return (
    <div className="ws" style={{ marginTop: 10 }}>
      <Field label="What we decided"><textarea value={x.decision} onChange={e => setX(v => ({ ...v, decision: e.target.value }))} /></Field>
      <Field label="Why (optional)"><textarea value={x.why} onChange={e => setX(v => ({ ...v, why: e.target.value }))} /></Field>
      <Field label="Date"><input type="date" value={x.decidedOn} onChange={e => setX(v => ({ ...v, decidedOn: e.target.value }))} /></Field>
      <div className="row"><button type="button" className="btn p s" onClick={submit}>Log decision</button></div>
    </div>
  );
}


// ─── Proof library (To-Do 13, 2026-09-29) ─────────────────────────────────────
// Every testimonial, ready to copy, with what the person agreed to. Photos need
// a description (accessibility rule). The six website quotes are seeded with
// permission "not recorded" until Team365 fills it in.
const SCOPE_LABELS = { website: 'Website', social: 'Social', ads: 'Ads' };
const HOW_LABELS = { in_writing: 'In writing', email: 'By email', message: 'By message', verbal: 'Said so (verbal)' };
const USED_LABELS = { website: 'On website', social: 'On social', ads: 'In ads', email: 'In email' };


// ─── Mini-lab, live (2026-10-06) ──────────────────────────────────────────
const PLATFORM_LABEL = { facebook: 'Facebook', instagram: 'Instagram', threads: 'Threads', bluesky: 'Bluesky', pinterest: 'Pinterest', linkedin: 'LinkedIn', google: 'Google Business' };
const DAY_LABEL = { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' };
const weekLabel = (iso) => `Week of ${new Date(`${iso}T12:00:00`).toLocaleDateString('en-CA', { month: 'long', day: 'numeric' })}`;

// Short names under the bars so they don't run together; the list below the chart uses the full names.
const NEEDLE_SHORT = { Instagram: 'Insta', 'Compass quiz': 'Quiz', 'Our links page': 'Links', 'Affiliate & partner links': 'Affiliates', 'Other sites': 'Other', 'Came straight to the site': 'Direct', LinkedIn: 'LinkedIn', Facebook: 'FB' };

function LabNeedle({ lab }) {
  const themes = (lab?.weeks || []).slice(0, 2).reverse().flatMap(w => w.themes);
  return (
    <Card live><p className="q">Is the needle moving?</p>
      <p className="qs">Tracking-link clicks per post, last two weeks. Green means it brought a join.</p>
      {!lab ? <p className="qs">Loading…</p> : themes.length === 0 ? <p className="qs">No posts recorded yet.</p> : (
        <VBars labels={themes.map(t => `${DAY_LABEL[t.day]} ${t.label}`)}
          series={[{ color: C.terra2, colors: themes.map(t => (t.joins > 0 ? C.forest : C.terra2)), vals: themes.map(t => t.clicks) }]}
          fmt={v => `${v}`} h={190} />
      )}
    </Card>
  );
}

const EXP_TONE = { keep: 'f', tweak: '', stop: 't', new: 't' };
const EXP_LABEL = { new: 'Just started', keep: 'Keep it', tweak: 'Tweak it', stop: 'Stop it' };

function LabExperiments({ lab, reload }) {
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const list = lab?.experiments || [];
  const save = async (e) => {
    e.preventDefault(); setBusy(true); setErr(null);
    try { await api.post('/admin/sales-marketing/experiments', { title, note }); setTitle(''); setNote(''); setAdding(false); reload(); }
    catch (x) { setErr(x.response?.data?.error || 'Could not save.'); } finally { setBusy(false); }
  };
  const setStatus = async (id, status) => { await api.put(`/admin/sales-marketing/experiments/${id}`, { status }).catch(() => {}); reload(); };
  const remove = async (id) => { await api.delete(`/admin/sales-marketing/experiments/${id}`).catch(() => {}); reload(); };
  return (
    <Card live style={{ marginTop: 16 }}><p className="q">Our experiments</p><p className="qs">Keep it, tweak it, or stop it</p>
      {list.length === 0 && !adding && <p className="qs">No experiments yet. Add the first one below.</p>}
      <div className="grid g4">
        {list.map(x => (
          <div key={x.id} className={`sticky ${EXP_TONE[x.status] || ''}`}>
            <b>{x.title}</b>{x.note ? <><br />{x.note}</> : null}
            <div className="row" style={{ marginTop: 8 }}>
              <select aria-label={`Status for ${x.title}`} value={x.status} onChange={e => setStatus(x.id, e.target.value)}>
                {Object.entries(EXP_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <button type="button" className="btn s" onClick={() => remove(x.id)}>Remove</button>
            </div>
          </div>
        ))}
      </div>
      {adding ? (
        <form onSubmit={save} className="grid" style={{ marginTop: 12 }}>
          <label className="fld">What are we trying?<input value={title} onChange={e => setTitle(e.target.value)} required maxLength={200} /></label>
          <label className="fld">Notes (optional)<textarea value={note} onChange={e => setNote(e.target.value)} maxLength={1000} /></label>
          {err && <p className="qs" style={{ color: 'var(--red)' }}>{err}</p>}
          <div className="row"><button type="submit" className="btn p" disabled={busy}>{busy ? 'Saving…' : 'Save experiment'}</button><button type="button" className="btn" onClick={() => setAdding(false)}>Cancel</button></div>
        </form>
      ) : (
        <div className="row" style={{ marginTop: 12 }}><button type="button" className="btn p" onClick={() => setAdding(true)}>+ New experiment</button></div>
      )}
    </Card>
  );
}

function LabKits({ lab }) {
  const weeks = lab?.weeks || [];
  const [wk, setWk] = useState(null);
  const [copied, setCopied] = useState(null);
  const current = weeks.find(w => w.week === wk) || weeks[0];
  if (!lab) return <Card live><p className="qs">Loading…</p></Card>;
  if (!current) return <Card live><p className="qs">No posts recorded yet.</p></Card>;
  const copy = (url) => { try { navigator.clipboard.writeText(url); setCopied(url); setTimeout(() => setCopied(null), 1500); } catch { /* fine */ } };
  return (
    <Card live>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <p className="q" style={{ margin: 0 }}>{weekLabel(current.week)}</p>
        <label className="fld" style={{ minWidth: 200 }}>Week
          <select value={current.week} onChange={e => setWk(e.target.value)}>
            {weeks.map(w => <option key={w.week} value={w.week}>{weekLabel(w.week)}</option>)}
          </select>
        </label>
      </div>
      <div className="labkit" style={{ marginTop: 14 }}>
        {current.themes.map(t => (
          <div key={t.themeKey} className="labday">
            <p className="labday-h">{DAY_LABEL[t.day]} · {t.label}</p>
            <div className="labimgs">
              {t.images.map(img => (
                <div key={img.url}>
                  <a href={img.url} target="_blank" rel="noopener noreferrer">
                    <img src={img.url} alt={img.alt || `${t.label} post picture (${PLATFORM_LABEL[img.platform] || img.platform})`} loading="lazy" />
                  </a>
                  <button type="button" className="btn s" onClick={() => copy(img.url)}>{copied === img.url ? 'Copied' : 'Copy link'}</button>
                </div>
              ))}
            </div>
            <p className="qs" style={{ margin: '8px 0 4px' }}>{t.clicks} clicks · {t.visits} visits · {t.checkouts} checkouts · {t.joins} joins</p>
            <div className="row">
              {t.posts.map((p, i) => p.url
                ? <a key={i} className="tag" href={p.url} target="_blank" rel="noopener noreferrer">{PLATFORM_LABEL[p.platform] || p.platform}{p.clicks != null ? ` · ${p.clicks}` : ''}</a>
                : <span key={i} className="tag">{PLATFORM_LABEL[p.platform] || p.platform}{p.clicks != null ? ` · ${p.clicks}` : ''}</span>)}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ─── This week's posts (Jenn, 2026-10-12): Monday's TikTok missed because,
// away from her desk, she couldn't copy the words, find the short link or get
// a right-size video. Everything for each post, ready to copy or save on a
// phone. Reads the weekly Metricool file (server: socialWeek.model.js).
const CH_NOTE = { TikTok: 'Put the link in your bio.', Instagram: 'Put the link in your bio.' };

function CopyBtn({ text, label }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); }
    catch { const t = document.createElement('textarea'); t.value = text; document.body.appendChild(t); t.select(); document.execCommand('copy'); t.remove(); }
    setDone(true); setTimeout(() => setDone(false), 2000);
  };
  return <button type="button" className="btn p s" onClick={copy}>{done ? 'Copied ✓' : label}</button>;
}

// Jenn, 2026-10-26: "There's no place for me to check your hashtags — which is what helps us get seen by
// non followers." The hashtags at the end of a post's words, as a list she can add to and remove from.
// Each channel has its own tags, so a change saves to this one post only (not the other channels).
const TAG_RUN = /((?:\s*#[A-Za-z0-9_]+)+)\s*$/;
const LIMITS = { Bluesky: 300, Threads: 500 };
function HashtagList({ post, onSaved, which = 'this' }) {
  const text = post.text || '';
  const run = text.match(TAG_RUN);
  const tags = run ? run[1].match(/#[A-Za-z0-9_]+/g) : [];
  const body = run ? text.slice(0, run.index) : text.replace(/\s+$/, '');
  const sep = run ? (run[1].match(/^\s*/)[0] || ' ') : '\n\n';
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const saveTags = async (next) => {
    const newText = next.length ? `${body}${sep}${next.join(' ')}` : body;
    if (LIMITS[post.platform] && newText.length > LIMITS[post.platform]) { setMsg(`That would make this ${post.platform} post ${newText.length} characters (${post.platform} allows ${LIMITS[post.platform]}). Remove a tag or shorten the words first.`); return; }
    setBusy(true); setMsg(null);
    try {
      await api.put(`/admin/sales-marketing/week-posts/${encodeURIComponent(post.id)}`, { text: newText, alsoSameTime: false, which });
      setMsg(next.length > tags.length ? `Added ${next[next.length - 1]} ✓` : 'Removed ✓'); onSaved();
    } catch (e) { setMsg(e.response?.data?.error || 'Could not save. Please try again.'); }
    setBusy(false);
  };
  const add = () => {
    const t = draft.trim().replace(/^#*/, '').replace(/[^A-Za-z0-9_]/g, '');
    if (!t) return;
    if (tags.some(x => x.toLowerCase() === `#${t.toLowerCase()}`)) { setMsg(`#${t} is already on this post.`); return; }
    setDraft(''); saveTags([...tags, `#${t}`]);
  };
  return (
    <div style={{ marginTop: 10 }}>
      <p className="qs" style={{ margin: 0 }}>Hashtags ({tags.length}) — how people who don't follow us find this post</p>
      {tags.length === 0 && <p className="qs" style={{ margin: '4px 0' }}>No hashtags on this post yet.</p>}
      <ul style={{ listStyle: 'none', padding: 0, margin: '6px 0' }}>
        {tags.map(t => (
          <li key={t} className="row" style={{ justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', border: '1px solid #F0EBE3', borderRadius: 8, marginBottom: 4, fontSize: 17 }}>
            <span>{t}</span>
            <button type="button" className="btn s" disabled={busy} onClick={() => saveTags(tags.filter(x => x !== t))} aria-label={`Remove ${t}`}>✕ Remove</button>
          </li>
        ))}
      </ul>
      <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <input value={draft} onChange={e => setDraft(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') add(); }} placeholder="#newhashtag"
          style={{ fontSize: 17, padding: '8px 10px', borderRadius: 8, border: '1px solid #C4714A', minWidth: 180 }} aria-label={`Add a hashtag to the ${post.platform} post`} />
        <button type="button" className="btn p s" disabled={busy || !draft.trim()} onClick={add}>{busy ? 'Saving…' : 'Add'}</button>
        {LIMITS[post.platform] && <span className="qs">{text.length} / {LIMITS[post.platform]} characters</span>}
      </div>
      {msg && <p className="qs" style={{ marginTop: 6 }}>{msg}</p>}
    </div>
  );
}

// Jenn, 2026-10-19: she reviews and edits the week's posts here from her iPad.
// Saving writes straight into this week's Metricool file (this-week.csv), the
// same file Claude schedules from. "Also change it on the other channels at
// this time" carries the changed words to Facebook, Instagram, TikTok, etc.
function EditableField({ post, field, label, value, copyLabel, onSaved, which = 'this' }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [all, setAll] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await api.put(`/admin/sales-marketing/week-posts/${encodeURIComponent(post.id)}`, { [field]: draft, alsoSameTime: all });
      const { others, skipped } = r.data;
      setEditing(false);
      setMsg(`Saved.${all && others ? ` Also changed on ${others} other channel${others === 1 ? '' : 's'} at ${post.time}.` : ''}${skipped.length ? ` Not changed on ${skipped.join(', ')} (too long for that channel — edit it there).` : ''}`);
      onSaved();
    } catch (e) { setMsg(e.response?.data?.error || 'Could not save. Please try again.'); }
    setBusy(false);
  };
  return (
    <div style={{ marginTop: 10 }}>
      <p className="qs" style={{ margin: 0 }}>{label}</p>
      {editing ? (
        <>
          <textarea value={draft} onChange={e => setDraft(e.target.value)} rows={Math.max(4, draft.split('\n').length + 2)}
            style={{ width: '100%', fontSize: 17, lineHeight: 1.5, padding: 12, borderRadius: 10, border: '1px solid #C4714A', marginTop: 6, boxSizing: 'border-box' }} aria-label={`${label} for ${post.platform}`} />
          <label className="qs" style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '6px 0' }}>
            <input type="checkbox" checked={all} onChange={e => setAll(e.target.checked)} /> Also change it on the other channels at {post.time}
          </label>
          <div className="row" style={{ gap: 8 }}>
            <button type="button" className="btn p s" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
            <button type="button" className="btn s" disabled={busy} onClick={() => { setDraft(value); setEditing(false); }}>Cancel</button>
          </div>
        </>
      ) : (
        <>
          <p style={{ whiteSpace: 'pre-wrap', margin: '4px 0 8px', fontSize: 17, lineHeight: 1.5 }}>{value || <span className="qs">Nothing here yet.</span>}</p>
          <div className="row" style={{ gap: 8 }}>
            <button type="button" className="btn s" onClick={() => { setDraft(value); setEditing(true); setMsg(null); }}>Edit</button>
            {value && <CopyBtn text={value} label={copyLabel} />}
          </div>
        </>
      )}
      {msg && <p className="qs" style={{ marginTop: 6 }}>{msg}</p>}
    </div>
  );
}

function WeekPostsPage({ which = 'this' }) {
  const title = which === 'next' ? "Next week's posts" : "This week's posts";
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [day, setDay] = useState(null);
  // Jenn, 2026-10-26: the home-screen app has no reload button, so refreshing meant Dashboard → Team365 →
  // Sales & Marketing → this page again. Refresh button + reload whenever she comes back to the app.
  const [loadedAt, setLoadedAt] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const load = (keepDay) => api.get(`/admin/sales-marketing/week-posts?which=${which}`).then(r => {
    setData(r.data);
    setLoadedAt(new Date());
    if (keepDay) return;
    const today = new Date().toLocaleDateString('en-CA');
    const dates = [...new Set(r.data.posts.map(p => p.date))];
    // Jenn, 2026-10-26: Refresh (or any reload) brings her back to the day she was working on, not today.
    let kept = null; try { kept = sessionStorage.getItem(`posts-day-${which}`); } catch { /* private mode */ }
    setDay(kept && dates.includes(kept) ? kept : dates.includes(today) ? today : dates.find(d => d > today) || dates[0] || null);
  }).catch(() => setErr(`Could not load ${title.toLowerCase()}.`));
  useEffect(() => { load(false); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const back = () => { if (document.visibilityState === 'visible') load(true); };
    document.addEventListener('visibilitychange', back);
    return () => document.removeEventListener('visibilitychange', back);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const refresh = () => { setRefreshing(true); load(true).finally(() => setRefreshing(false)); };
  // Pictures get replaced under the same name sometimes; the load time makes the browser fetch them fresh.
  const fresh = (url) => (loadedAt ? `${url}${url.includes('?') ? '&' : '?'}v=${loadedAt.getTime()}` : url);
  if (err) return <section><h2 className="ptitle">{title}</h2><div className="note">{err}</div></section>;
  if (!data) return <section><h2 className="ptitle">{title}</h2><p className="pq">Loading…</p></section>;
  const dates = [...new Set(data.posts.map(p => p.date))];
  const fmtDay = (d) => new Date(`${d}T12:00:00`).toLocaleDateString('en-CA', { weekday: 'long', month: 'short', day: 'numeric' });
  const now = new Date();
  return (
    <section>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <h2 className="ptitle" style={{ margin: 0 }}>{title}</h2>
        <div className="row" style={{ alignItems: 'center', gap: 10 }}>
          {loadedAt && <span className="qs">Updated {loadedAt.toLocaleTimeString('en-CA', { hour: 'numeric', minute: '2-digit' })}</span>}
          <button type="button" className="chip on" onClick={refresh} disabled={refreshing}>{refreshing ? 'Refreshing…' : '↻ Refresh'}</button>
        </div>
      </div>
      {/* Jenn, 2026-10-20: admin weeks run Sunday to Saturday, like the summaries and events. */}
      {data.from && <p className="q" style={{ margin: '0 0 4px' }}>{fmtDay(data.from)} – {fmtDay(data.to)}</p>}
      <p className="pq">Everything for each post — words, link and picture. Tap Edit to change the words; your change saves straight into {which === 'next' ? "next week's" : "this week's"} Metricool file.{which === 'next' && ' Reviewed Thursdays at the work-along; Claude schedules it on Friday. On Sunday it moves to This week\'s posts by itself.'}</p>
      {dates.length === 0 ? <div className="empty">{title} aren't loaded yet.</div> : (
        <>
          <div className="chips">{dates.map(d => (
            <button type="button" key={d} className={`chip ${day === d ? 'on' : ''}`} onClick={() => { setDay(d); try { sessionStorage.setItem(`posts-day-${which}`, d); } catch { /* private mode */ } }}>{fmtDay(d).split(',')[0]}</button>
          ))}</div>
          <p className="q" style={{ marginTop: 6 }}>{day && fmtDay(day)}</p>
          {(() => {
            // Jenn, 2026-10-19: "I don't see the other streams for Monday." Each post is
            // repeated once per channel, so group by time with a heading and jump buttons.
            const dayPosts = data.posts.filter(p => p.date === day);
            const times = [...new Set(dayPosts.map(p => p.time))].sort();
            const fmtT = (t) => { const [h, m] = t.split(':').map(Number); return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${h < 12 ? 'am' : 'pm'}`; };
            const headOf = (grp) => ((grp.find(p => p.platform === 'Facebook') || grp[0]).text || '').split('\n')[0];
            return (<>
              {times.length > 1 && <div className="chips" style={{ marginBottom: 8 }}>{times.map(t => (
                <button type="button" key={t} className="chip" onClick={() => document.getElementById(`slot-${t}`)?.scrollIntoView({ behavior: 'smooth' })}>{fmtT(t)}</button>
              ))}</div>}
              {times.map(t => { const grp = dayPosts.filter(p => p.time === t); return (
              <div key={t} id={`slot-${t}`} style={{ marginTop: 20, scrollMarginTop: 12 }}>
                <h3 style={{ fontSize: 20, margin: '0 0 4px' }}>{fmtT(t)} — {headOf(grp)}</h3>
                <p className="qs" style={{ margin: '0 0 10px' }}>The same post on {grp.length} channel{grp.length === 1 ? '' : 's'}: {grp.map(p => p.platform).join(', ')}</p>
              {grp.map(p => {
            const link = p.linkCode ? `https://365reset.com/go/${p.linkCode}` : null;
            const due = new Date(`${p.date}T${p.time || '00:00'}:00`) < now;
            return (
              <div className="card" key={p.id} style={{ marginBottom: 12 }}>
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <p className="q" style={{ margin: 0 }}>{p.platform}{p.postType ? ` · ${p.postType}` : ''}</p>
                  <span className={`tag ${p.posted ? 'g' : ''}`}>
                    {p.posted ? 'Posted ✓' : due ? 'Not confirmed as posted yet' : `Goes out ${p.time}`}
                  </span>
                </div>
                {p.posted && p.publicUrl && <a className="qs" href={p.publicUrl} target="_blank" rel="noopener noreferrer">See the post</a>}
                {CH_NOTE[p.platform] && <p className="qs" style={{ marginTop: 6 }}>{CH_NOTE[p.platform]}</p>}
                {p.media.length === 0
                  ? <div className="note">Picture or video not added yet.</div>
                  : <div className="row" style={{ marginTop: 8, flexWrap: 'wrap' }}>{p.media.map((m, n) => (
                    <div key={n} style={{ width: '100%', maxWidth: 420 }}>
                      {m.video
                        ? <video src={m.url} controls playsInline style={{ width: '100%', borderRadius: 10 }} aria-label={m.alt || `${p.platform} video`} />
                        : <img src={fresh(m.url)} alt={m.alt || ''} style={{ width: '100%', borderRadius: 10 }} />}
                      <a className="btn s" href={m.url} download target="_blank" rel="noopener noreferrer" style={{ marginTop: 6, display: 'inline-block' }}>Save {m.video ? 'video' : 'picture'}</a>
                    </div>
                  ))}</div>}
                {p.platform === 'TikTok' && <EditableField key={`tt${p.id}`} post={p} field="tiktokTitle" label="TikTok title" value={p.tiktokTitle} copyLabel="Copy title" onSaved={() => load(true)} which={which} />}
                {p.pinTitle && <div style={{ marginTop: 10 }}><p className="qs" style={{ margin: 0 }}>Pin title</p><p style={{ margin: '4px 0' }}>{p.pinTitle}</p><CopyBtn text={p.pinTitle} label="Copy pin title" /></div>}
                <EditableField key={`w${p.id}`} post={p} field="text" label="Words" value={p.text} copyLabel="Copy words" onSaved={() => load(true)} which={which} />
                <HashtagList key={`h${p.id}`} post={p} onSaved={() => load(true)} which={which} />
                {(p.firstComment || ['Facebook', 'TikTok', 'LinkedIn'].includes(p.platform)) && <EditableField key={`c${p.id}`} post={p} field="firstComment" label="First comment" value={p.firstComment} copyLabel="Copy comment" onSaved={() => load(true)} which={which} />}
                <div style={{ marginTop: 10 }}>
                  <p className="qs" style={{ margin: 0 }}>Short link</p>
                  {link ? <><p style={{ margin: '4px 0 8px', wordBreak: 'break-all' }}><b>{link.replace('https://', '')}</b></p><CopyBtn text={link} label="Copy link" /></>
                    : <p className="qs">No short link made for this one yet.</p>}
                </div>
              </div>
            );
          })}
              </div>); })}
            </>);
          })()}
        </>
      )}
    </section>
  );
}

// ─── Market pulse (Jenn, 2026-10-12): real, sourced research replacing CB's
// example charts. Each card is a row in market_pulse_cards (migration 169);
// Team365 edits any card with its Edit button, Claude updates the same rows
// from new research. Every card shows its sources and dates.
const pulseFmt = (unit) => (v) => (unit === '$' ? `$${v}` : `${v}${unit || ''}`);

// ─── Brand & vision (Jenn, 2026-10-20): "set up those boards for me to think through this process
// ... I need to look at it as a whole to get the nuances down." One board per area: our beliefs #1–#5
// (the "Our philosophy on ___" posts), what we actually offer there, and nuances. Talking points for
// presenters, instructors and marketing. Claude reads these before writing any post (migration 178).
// ─── My links page (Jenn, 2026-10-20): 365reset.com/my-links replaces a link tree in our social
// bios. Team365 edits the list here; Save writes the whole list (order = list order). Migration 179.
function MyLinksPage() {
  const [links, setLinks] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const load = useCallback(() => {
    api.get('/admin/sales-marketing/my-links').then(r => setLinks(r.data.links)).catch(() => setMsg('Could not load the links.'));
  }, []);
  useEffect(() => { load(); }, [load]);
  const set = (i, k, v) => setLinks(ls => ls.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const move = (i, d) => setLinks(ls => { const n = [...ls]; const j = i + d; if (j < 0 || j >= n.length) return ls; [n[i], n[j]] = [n[j], n[i]]; return n; });
  const save = async () => {
    setBusy(true); setMsg(null);
    try { await api.put('/admin/sales-marketing/my-links', { links }); setMsg('Saved. The page shows your changes right away.'); load(); }
    catch (e) { setMsg(e.response?.data?.error || 'Could not save. Please try again.'); }
    setBusy(false);
  };
  const box = { width: '100%', fontSize: 17, padding: 10, borderRadius: 10, border: '1px solid #C4714A', marginTop: 4, boxSizing: 'border-box' };
  return (
    <section>
      <h2 className="ptitle">My links page</h2>
      <p className="pq">The page in our social media bios, in place of a link tree: <a href="https://365reset.com/my-links" target="_blank" rel="noreferrer">365reset.com/my-links</a>. The first link shows as the big orange button. Change anything below, then tap Save.</p>
      {msg && <div className="note">{msg}</div>}
      {!links ? <p className="pq">Loading…</p> : (
        <>
          {links.map((l, i) => (
            <Card live key={i} style={{ marginTop: 12 }}>
              <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                <p className="q" style={{ margin: 0 }}>Link {i + 1}</p>
                <span className="row" style={{ gap: 6 }}>
                  <button type="button" className="btn s" disabled={i === 0} onClick={() => move(i, -1)} aria-label={`Move link ${i + 1} up`}>↑</button>
                  <button type="button" className="btn s" disabled={i === links.length - 1} onClick={() => move(i, 1)} aria-label={`Move link ${i + 1} down`}>↓</button>
                  <button type="button" className="btn s" onClick={() => setLinks(ls => ls.filter((_, j) => j !== i))}>Remove</button>
                </span>
              </div>
              <label className="qs" style={{ display: 'block', marginTop: 8 }}>Button words<input value={l.label || ''} onChange={e => set(i, 'label', e.target.value)} style={box} /></label>
              <label className="qs" style={{ display: 'block', marginTop: 8 }}>Small line under it (optional)<input value={l.sub || ''} onChange={e => set(i, 'sub', e.target.value)} style={box} /></label>
              <label className="qs" style={{ display: 'block', marginTop: 8 }}>Web address (starts with https://)<input value={l.url || ''} onChange={e => set(i, 'url', e.target.value)} style={box} inputMode="url" /></label>
              <label className="qs" style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
                <input type="checkbox" checked={l.visible !== false} onChange={e => set(i, 'visible', e.target.checked)} /> Show on the page
              </label>
            </Card>
          ))}
          <div className="row" style={{ gap: 8, marginTop: 14 }}>
            <button type="button" className="btn s" onClick={() => setLinks(ls => [...ls, { label: '', sub: '', url: 'https://', visible: true }])}>+ Add a link</button>
            <button type="button" className="btn p s" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
          </div>
        </>
      )}
    </section>
  );
}

function BrandVisionPage() {
  const [boards, setBoards] = useState(null);
  const [editing, setEditing] = useState(null);
  const [msg, setMsg] = useState(null);
  const [newBoard, setNewBoard] = useState(null);
  const load = useCallback(() => {
    api.get('/admin/sales-marketing/brand-boards').then(r => setBoards(r.data.boards)).catch(() => setMsg('Could not load Brand & vision.'));
  }, []);
  useEffect(() => { load(); }, [load]);
  const filled = (boards || []).filter(b => b.board_key !== 'voice').reduce((n, b) => n + (b.beliefs || []).filter(x => x).length, 0);
  const addBoard = async () => {
    try { await api.post('/admin/sales-marketing/brand-boards', { title: newBoard }); setNewBoard(null); load(); }
    catch (e) { setMsg(e.response?.data?.error || 'Could not add the board.'); }
  };
  return (
    <section>
      <h2 className="ptitle">Brand & vision</h2>
      <p className="pq">What we believe about each part of life, what we actually offer there, and how we sound. Our talking points for presenters, new instructors and anyone doing our marketing — and where every "Our philosophy on…" post comes from.</p>
      <div className="card" style={{ background: 'var(--sand)', borderColor: 'var(--sand-2)' }}>
        <p className="q">How this works</p>
        <p className="qs" style={{ margin: 0 }}>Tap Edit on any board to add as many beliefs as you like, add your own fields, or change anything. Add a whole new board at the bottom. Every belief has a Copy button. Claude only uses what is written here{boards ? ` · ${filled} beliefs written so far` : ''}.</p>
      </div>
      {msg && <div className="note">{msg}</div>}
      {!boards ? <p className="pq">Loading…</p> : (
        <div className="grid g2" style={{ marginTop: 16 }}>
          {boards.map(b => (editing === b.board_key
            ? <BrandBoardForm key={b.board_key} board={b} onDone={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />
            : <BrandBoardCard key={b.board_key} board={b} onEdit={() => setEditing(b.board_key)} />))}
        </div>
      )}
      {newBoard !== null ? (
        <div className="card" style={{ marginTop: 14 }}>
          <label className="qs">New board name<input value={newBoard} onChange={e => setNewBoard(e.target.value)} style={{ width: '100%', fontSize: 17, padding: 10, borderRadius: 10, border: '1px solid #C4714A', marginTop: 4, boxSizing: 'border-box' }} placeholder="Example: Christmas campaign" /></label>
          <div className="row" style={{ gap: 8, marginTop: 10 }}>
            <button type="button" className="btn p s" disabled={!newBoard.trim()} onClick={addBoard}>Add board</button>
            <button type="button" className="btn s" onClick={() => setNewBoard(null)}>Cancel</button>
          </div>
        </div>
      ) : <button type="button" className="btn s" style={{ marginTop: 14 }} onClick={() => setNewBoard('')}>+ Add a board</button>}
      <BrandLibrary />
    </section>
  );
}

function BrandBoardCard({ board, onEdit }) {
  const voice = board.board_key === 'voice';
  const beliefs = (board.beliefs || []).filter(x => x);
  return (
    <Card live>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div><p className="q">{board.title}</p>{board.about && <p className="qs">{board.about}</p>}</div>
        <span className="row" style={{ gap: 6 }}>
          {beliefs.length > 1 && <CopyButton text={beliefs.map((x, i) => `${i + 1}. ${x}`).join('\n')} label="Copy all" />}
          <button type="button" className="btn s" onClick={onEdit}>Edit</button>
        </span>
      </div>
      <p className="qs" style={{ margin: '10px 0 4px', fontWeight: 600 }}>{voice ? 'Our rules' : 'What we believe'}</p>
      {(board.beliefs || []).length === 0 && <p className="qs" style={{ margin: 0 }}>None yet. Tap Edit to add one.</p>}
      <ol style={{ margin: 0, paddingLeft: 24, lineHeight: 1.6 }}>
        {(board.beliefs || []).map((x, i) => (
          <li key={i} style={{ marginBottom: 6 }}>
            {x ? <span>{x} <CopyButton text={x} /></span> : <span className="qs">Not written yet</span>}
          </li>
        ))}
      </ol>
      {board.offers && (<>
        <p className="qs" style={{ margin: '12px 0 4px', fontWeight: 600 }}>What we offer here <CopyButton text={board.offers} /></p>
        <p style={{ whiteSpace: 'pre-wrap', margin: 0, lineHeight: 1.6 }}>{board.offers}</p>
      </>)}
      {(board.extra_fields || []).map((f, i) => (
        <React.Fragment key={i}>
          <p className="qs" style={{ margin: '12px 0 4px', fontWeight: 600 }}>{f.label || 'Untitled'} {f.value && <CopyButton text={f.value} />}</p>
          <p style={{ whiteSpace: 'pre-wrap', margin: 0, lineHeight: 1.6 }}>{f.value || <span className="qs">Not written yet</span>}</p>
        </React.Fragment>
      ))}
      {board.notes && (<>
        <p className="qs" style={{ margin: '12px 0 4px', fontWeight: 600 }}>Nuances & notes</p>
        <p style={{ whiteSpace: 'pre-wrap', margin: 0, lineHeight: 1.6 }}>{board.notes}</p>
      </>)}
    </Card>
  );
}

function BrandBoardForm({ board, onDone, onCancel }) {
  const voice = board.board_key === 'voice';
  const [title, setTitle] = useState(board.title || '');
  const [about, setAbout] = useState(board.about || '');
  const [beliefs, setBeliefs] = useState((board.beliefs || []).length ? [...board.beliefs] : ['']);
  const [offers, setOffers] = useState(board.offers || '');
  const [notes, setNotes] = useState(board.notes || '');
  const [fields, setFields] = useState(board.extra_fields || []);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const box = { width: '100%', fontSize: 17, lineHeight: 1.5, padding: 12, borderRadius: 10, border: '1px solid #C4714A', marginTop: 4, boxSizing: 'border-box' };
  const move = (i, d) => { const n = [...beliefs]; const j = i + d; if (j < 0 || j >= n.length) return; [n[i], n[j]] = [n[j], n[i]]; setBeliefs(n); };
  const setField = (i, k, v) => setFields(fs => fs.map((f, j) => (j === i ? { ...f, [k]: v } : f)));
  const save = async () => {
    setBusy(true); setErr(null);
    try { await api.put(`/admin/sales-marketing/brand-boards/${board.board_key}`, { title, about, beliefs, offers, notes, extraFields: fields }); onDone(); }
    catch (e) { setErr(e.response?.data?.error || 'Could not save. Please try again.'); setBusy(false); }
  };
  const putAway = async () => {
    if (!window.confirm(`Put away the "${board.title}" board? It's kept, just hidden.`)) return;
    await api.post(`/admin/sales-marketing/brand-boards/${board.board_key}/archive`); onDone();
  };
  return (
    <Card live>
      <label className="qs">Board name<input value={title} onChange={e => setTitle(e.target.value)} style={box} /></label>
      <label className="qs" style={{ display: 'block', marginTop: 10 }}>Short description<input value={about} onChange={e => setAbout(e.target.value)} style={box} /></label>
      <p className="qs" style={{ margin: '12px 0 0', fontWeight: 600 }}>{voice ? 'Our rules' : 'What we believe'}</p>
      {beliefs.map((x, i) => (
        <div key={i} style={{ marginTop: 8 }}>
          <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <span className="qs">#{i + 1}</span>
            <span className="row" style={{ gap: 6 }}>
              <button type="button" className="btn s" disabled={i === 0} onClick={() => move(i, -1)} aria-label={`Move #${i + 1} up`}>↑</button>
              <button type="button" className="btn s" disabled={i === beliefs.length - 1} onClick={() => move(i, 1)} aria-label={`Move #${i + 1} down`}>↓</button>
              <button type="button" className="btn s" onClick={() => setBeliefs(b => b.filter((_, j) => j !== i))} aria-label={`Remove #${i + 1}`}>Remove</button>
            </span>
          </div>
          <textarea value={x} rows={2} onChange={e => { const n = [...beliefs]; n[i] = e.target.value; setBeliefs(n); }} style={box} aria-label={`${board.title} #${i + 1}`} />
        </div>
      ))}
      <button type="button" className="btn s" style={{ marginTop: 8 }} onClick={() => setBeliefs(b => [...b, ''])}>+ Add a belief</button>
      {!voice && <label className="qs" style={{ display: 'block', marginTop: 12 }}>What we offer here<textarea value={offers} rows={Math.max(4, offers.split('\n').length + 1)} onChange={e => setOffers(e.target.value)} style={box} /></label>}
      {fields.map((f, i) => (
        <div key={i} style={{ marginTop: 12, padding: 10, border: '1px dashed #d9cfc4', borderRadius: 10 }}>
          <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
            <input value={f.label} onChange={e => setField(i, 'label', e.target.value)} placeholder="Field name" style={{ ...box, marginTop: 0, flex: 1 }} aria-label="Field name" />
            <button type="button" className="btn s" onClick={() => setFields(fs => fs.filter((_, j) => j !== i))}>Remove</button>
          </div>
          <textarea value={f.value} rows={Math.max(3, (f.value || '').split('\n').length + 1)} onChange={e => setField(i, 'value', e.target.value)} style={box} aria-label={f.label || 'Field'} />
        </div>
      ))}
      <button type="button" className="btn s" style={{ marginTop: 10 }} onClick={() => setFields(fs => [...fs, { label: '', value: '' }])}>+ Add a field</button>
      <label className="qs" style={{ display: 'block', marginTop: 12 }}>Nuances & notes<textarea value={notes} rows={Math.max(4, notes.split('\n').length + 1)} onChange={e => setNotes(e.target.value)} style={box} /></label>
      {err && <div className="note">{err}</div>}
      <div className="row" style={{ gap: 8, marginTop: 12 }}>
        <button type="button" className="btn p s" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
        <button type="button" className="btn s" disabled={busy} onClick={onCancel}>Cancel</button>
        {board.is_custom && <button type="button" className="btn s" disabled={busy} onClick={putAway}>Put away this board</button>}
      </div>
    </Card>
  );
}

function MarketPulsePage() {
  const [cards, setCards] = useState(null);
  const [editing, setEditing] = useState(null);
  const [msg, setMsg] = useState(null);
  const load = useCallback(() => {
    api.get('/admin/sales-marketing/market-pulse').then(r => setCards(r.data.cards)).catch(() => setMsg('Could not load Market pulse.'));
  }, []);
  useEffect(() => { load(); }, [load]);
  const updated = (cards || []).map(c => c.updated_at).sort().pop();
  return (
    <section>
      <h2 className="ptitle">Market pulse</h2>
      <p className="pq">What worries people, what others charge, what a normal join rate is, and what helps people say yes.</p>
      <div className="card" style={{ background: 'var(--sand)', borderColor: 'var(--sand-2)' }}>
        <p className="q">Where this comes from</p>
        <p className="qs" style={{ margin: 0 }}>Published surveys, industry reports and benchmarks. Every card shows its sources and dates, and where its numbers are from. Nothing is estimated. Anyone on Team365 can change a card with its Edit button.
          {updated && ` Last changed ${new Date(updated).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' })}.`}</p>
      </div>
      {msg && <div className="note">{msg}</div>}
      {!cards ? <p className="pq">Loading…</p> : (
        <div className="grid g2" style={{ marginTop: 16 }}>
          {cards.map(c => (editing === c.card_key
            ? <MarketPulseForm key={c.card_key} card={c} onDone={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />
            : <MarketPulseCard key={c.card_key} card={c} onEdit={() => setEditing(c.card_key)} />))}
        </div>
      )}
    </section>
  );
}

function MarketPulseCard({ card, onEdit }) {
  const items = card.items || [];
  const bars = card.kind === 'bars' && items.some(i => i.value != null);
  const shownLines = items.filter(i => i.shown);
  return (
    <Card live>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div><p className="q">{card.title}</p>{card.subtitle && <p className="qs">{card.subtitle}</p>}</div>
        <button type="button" className="btn s" onClick={onEdit}>Edit</button>
      </div>
      {bars ? (
        <VBars labels={items.map(i => i.label)} series={[{ color: C.terra, colors: items.map(i => (i.highlight ? C.forest : C.terra)), vals: items.map(i => i.value) }]} fmt={pulseFmt(card.unit)} />
      ) : (
        <ul style={{ margin: '8px 0 0', paddingLeft: 20, lineHeight: 1.7 }}>{items.map((i, n) => <li key={n}>{i.label}</li>)}</ul>
      )}
      {bars && shownLines.length > 0 && (
        <ul className="qs" style={{ margin: '10px 0 0', paddingLeft: 18 }}>{shownLines.map((i, n) => <li key={n}><b>{i.label}:</b> {i.shown}</li>)}</ul>
      )}
      {card.note && <p className="qs" style={{ marginTop: 10 }}>{card.note}</p>}
      <div className="src">
        {(card.sources || []).length === 0 ? 'No source added yet' : card.sources.map((s, n) => (
          <span key={n}>{n > 0 && ' · '}{s.url ? <a href={s.url} target="_blank" rel="noopener noreferrer">{s.name}</a> : s.name}{s.date && `, ${s.date}`}</span>
        ))}
      </div>
    </Card>
  );
}

function MarketPulseForm({ card, onDone, onCancel }) {
  const [f, setF] = useState({
    title: card.title || '', subtitle: card.subtitle || '', note: card.note || '',
    items: (card.items || []).map(i => ({ label: i.label || '', value: i.value ?? '', shown: i.shown || '', highlight: !!i.highlight })),
    sources: (card.sources || []).map(s => ({ name: s.name || '', url: s.url || '', date: s.date || '' })),
  });
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const setRow = (list, n, k, v) => setF(x => ({ ...x, [list]: x[list].map((r, i) => (i === n ? { ...r, [k]: v } : r)) }));
  const addRow = (list, blank) => setF(x => ({ ...x, [list]: [...x[list], blank] }));
  const dropRow = (list, n) => setF(x => ({ ...x, [list]: x[list].filter((_, i) => i !== n) }));
  const bars = card.kind === 'bars';
  const submit = async () => {
    setSaving(true); setErr(null);
    try { await api.put(`/admin/sales-marketing/market-pulse/${card.card_key}`, f); onDone(); }
    catch (e) { setErr(e.response?.data?.error || 'Could not save.'); } finally { setSaving(false); }
  };
  return (
    <div className="card" style={{ gridColumn: '1 / -1' }}>
      <p className="q">Edit "{card.title}"</p>
      {err && <div className="note">{err}</div>}
      <div className="ws" style={{ marginTop: 10 }}>
        <Field label="The question"><input value={f.title} onChange={e => set('title', e.target.value)} /></Field>
        <Field label="Small line under it"><input value={f.subtitle} onChange={e => set('subtitle', e.target.value)} /></Field>
      </div>
      <p className="q" style={{ fontSize: 15, marginTop: 14 }}>{bars ? 'Bars' : 'Lines'}</p>
      {f.items.map((r, n) => (
        <div className="row" key={n} style={{ marginBottom: 8, alignItems: 'flex-end' }}>
          <Field label="What it is"><input value={r.label} onChange={e => setRow('items', n, 'label', e.target.value)} /></Field>
          {bars && <Field label={`Number${card.unit === '$' ? ' (US$)' : card.unit ? ` (${card.unit})` : ''}`}><input type="number" step="any" style={{ width: 100 }} value={r.value} onChange={e => setRow('items', n, 'value', e.target.value)} /></Field>}
          {bars && <Field label="Words to show (optional)"><input value={r.shown} onChange={e => setRow('items', n, 'shown', e.target.value)} placeholder="e.g. US$150–500" /></Field>}
          {bars && <label className="chip" style={{ cursor: 'pointer' }}><input type="checkbox" checked={r.highlight} onChange={e => setRow('items', n, 'highlight', e.target.checked)} /> 365reset (green)</label>}
          <button type="button" className="btn s" onClick={() => dropRow('items', n)}>Remove</button>
        </div>
      ))}
      <button type="button" className="btn s" onClick={() => addRow('items', { label: '', value: '', shown: '', highlight: false })}>+ Add a {bars ? 'bar' : 'line'}</button>
      <p className="q" style={{ fontSize: 15, marginTop: 14 }}>Sources</p>
      {f.sources.map((s, n) => (
        <div className="row" key={n} style={{ marginBottom: 8, alignItems: 'flex-end' }}>
          <Field label="Who published it"><input value={s.name} onChange={e => setRow('sources', n, 'name', e.target.value)} /></Field>
          <Field label="Link"><input value={s.url} onChange={e => setRow('sources', n, 'url', e.target.value)} placeholder="https://" /></Field>
          <Field label="Date"><input style={{ width: 110 }} value={s.date} onChange={e => setRow('sources', n, 'date', e.target.value)} placeholder="e.g. 2026" /></Field>
          <button type="button" className="btn s" onClick={() => dropRow('sources', n)}>Remove</button>
        </div>
      ))}
      <button type="button" className="btn s" onClick={() => addRow('sources', { name: '', url: '', date: '' })}>+ Add a source</button>
      <div style={{ marginTop: 14 }}><Field label="Note under the card"><textarea value={f.note} onChange={e => set('note', e.target.value)} /></Field></div>
      <div className="row" style={{ marginTop: 14 }}>
        <button type="button" className="btn p" disabled={saving} onClick={submit}>{saving ? 'Saving…' : 'Save'}</button>
        <button type="button" className="btn" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function ProofPage() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState('All');
  const [editing, setEditing] = useState(null); // null | 'new' | testimonial
  const [msg, setMsg] = useState(null);
  const [copied, setCopied] = useState(null);
  const load = useCallback(() => {
    api.get('/admin/sales-marketing/testimonials').then(r => setItems(r.data.testimonials)).catch(() => setMsg('Could not load the Proof library.'));
  }, []);
  useEffect(() => { load(); }, [load]);
  const topics = [...new Set((items || []).map(t => t.topic).filter(Boolean))].sort();
  const shown = (items || []).filter(t => filter === 'All' || (filter === 'Not used yet' ? !(t.used_on || []).length : t.topic === filter));
  const copy = async (t) => {
    try { await navigator.clipboard.writeText(`"${t.quote}" — ${t.person_name}`); setCopied(t.id); setTimeout(() => setCopied(null), 2000); } catch { /* copy by hand */ }
  };
  return (
    <section>
      <h2 className="ptitle">Proof library</h2>
      <p className="pq">Every review and testimonial, ready to copy into a post, page or email.</p>
      <div style={{ marginBottom: 8 }}><Status live /></div>
      <div className="row" style={{ marginBottom: 14 }}>
        <button type="button" className="btn" disabled title="In development">Request a testimonial · in development</button>
        <button type="button" className="btn p" onClick={() => setEditing('new')}>+ Add one</button>
      </div>
      {msg && <div className="note">{msg}</div>}
      {editing && <TestimonialForm item={editing === 'new' ? null : editing} topics={topics} onDone={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />}
      <div className="chips">{['All', ...topics, 'Not used yet'].map(c => (
        <button type="button" key={c} className={`chip ${filter === c ? 'on' : ''}`} onClick={() => setFilter(c)}>{c}</button>
      ))}</div>
      {!items ? <p className="pq">Loading…</p> : shown.length === 0 ? <div className="empty">Nothing here yet.</div> : (
        <div className="proof">
          {shown.map(t => {
            const approved = (t.permission_scope || []).length > 0;
            return (
              <div className="pq2" key={t.id}>
                {t.photo_url
                  ? <img src={t.photo_url} alt={t.photo_alt_text || ''} style={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 10 }} />
                  : <div className="slot">No photo<br /><small>add one with permission</small></div>}
                <p>"{t.quote}"</p><b>{t.person_name}</b>
                <div className="row">{t.topic && <span className="tag">{t.topic}</span>}{(t.used_on || []).map(u => <span key={u} className="tag g">{USED_LABELS[u] || u}</span>)}</div>
                <div className="perm">
                  <span className={`tag ${approved ? 'g' : 'a'}`}>{approved ? `Approved for: ${t.permission_scope.map(x => SCOPE_LABELS[x]).join(', ')}` : 'Permission not recorded'}</span>
                  {approved && <span className="tag">{[HOW_LABELS[t.permission_how], t.permission_date, t.permission_proof_url && 'copy saved'].filter(Boolean).join(' · ') || 'How and when not recorded'}</span>}
                </div>
                <div className="row">
                  <button type="button" className="btn s" onClick={() => copy(t)}>{copied === t.id ? 'Copied ✓' : 'Copy'}</button>
                  <button type="button" className="btn s" onClick={() => setEditing(t)}>{approved ? 'Edit' : 'Edit · record approval'}</button>
                  <button type="button" className="btn s" disabled title="In development">Make a post · in development</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div className="note" style={{ marginTop: 14 }}>The six quotes from 365reset.com are real, word for word. Record each person's approval with "Edit" before using their words in social posts or ads.</div>
    </section>
  );
}

function TestimonialForm({ item, topics, onDone, onCancel }) {
  const [f, setF] = useState({
    quote: item?.quote || '', personName: item?.person_name || '', topic: item?.topic || '',
    photoUrl: item?.photo_url || '', photoAltText: item?.photo_alt_text || '',
    permissionScope: item?.permission_scope || [], permissionHow: item?.permission_how || '', permissionDate: item?.permission_date || '',
    permissionProofUrl: item?.permission_proof_url || '', usedOn: item?.used_on || [],
  });
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const toggle = (k, v) => setF(x => ({ ...x, [k]: x[k].includes(v) ? x[k].filter(y => y !== v) : [...x[k], v] }));
  const submit = async () => {
    setSaving(true); setErr(null);
    try {
      if (item) await api.put(`/admin/sales-marketing/testimonials/${item.id}`, f);
      else await api.post('/admin/sales-marketing/testimonials', f);
      onDone();
    } catch (e) { setErr(e.response?.data?.error || 'Could not save.'); } finally { setSaving(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Remove ${item.person_name}'s testimonial from the Proof library?`)) return;
    try { await api.delete(`/admin/sales-marketing/testimonials/${item.id}`); onDone(); } catch { setErr('Could not remove it.'); }
  };
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <p className="q">{item ? `Edit ${item.person_name}'s testimonial` : 'Add a testimonial'}</p>
      {err && <div className="note">{err}</div>}
      <div className="ws" style={{ marginTop: 10 }}>
        <Field label="Their words"><textarea value={f.quote} onChange={e => set('quote', e.target.value)} /></Field>
        <div>
          <Field label="Who said it"><input value={f.personName} onChange={e => set('personName', e.target.value)} /></Field>
          <div style={{ height: 10 }} />
          <Field label="Topic"><input list="proof-topics" value={f.topic} onChange={e => set('topic', e.target.value)} placeholder="e.g. Community" /></Field>
          <datalist id="proof-topics">{topics.map(t => <option key={t} value={t} />)}</datalist>
        </div>
        <Field label="Photo (only with their permission)">
          <ImageDropZone currentUrl={f.photoUrl || null} onUploaded={u => set('photoUrl', u)} onClear={() => set('photoUrl', '')} />
        </Field>
        {f.photoUrl && <Field label="Describe the photo (for screen readers)"><input value={f.photoAltText} onChange={e => set('photoAltText', e.target.value)} placeholder="e.g. Paula smiling on her porch with a mug of tea" /></Field>}
      </div>
      <p className="q" style={{ fontSize: 15, marginTop: 14 }}>What they agreed to</p>
      <div className="row">{Object.entries(SCOPE_LABELS).map(([k, v]) => (
        <label key={k} className="chip" style={{ cursor: 'pointer' }}><input type="checkbox" checked={f.permissionScope.includes(k)} onChange={() => toggle('permissionScope', k)} /> {v}</label>
      ))}</div>
      <div className="ws" style={{ marginTop: 10 }}>
        <Field label="How they agreed"><select value={f.permissionHow} onChange={e => set('permissionHow', e.target.value)}>
          <option value="">Not recorded</option>{Object.entries(HOW_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select></Field>
        <Field label="When"><input type="date" value={f.permissionDate} onChange={e => set('permissionDate', e.target.value)} /></Field>
        <Field label="A copy of their approval (screenshot)">
          <ImageDropZone currentUrl={f.permissionProofUrl || null} onUploaded={u => set('permissionProofUrl', u)} onClear={() => set('permissionProofUrl', '')} />
        </Field>
      </div>
      <p className="q" style={{ fontSize: 15, marginTop: 14 }}>Where we've used it</p>
      <div className="row">{Object.entries(USED_LABELS).map(([k, v]) => (
        <label key={k} className="chip" style={{ cursor: 'pointer' }}><input type="checkbox" checked={f.usedOn.includes(k)} onChange={() => toggle('usedOn', k)} /> {v}</label>
      ))}</div>
      <div className="row" style={{ marginTop: 14 }}>
        <button type="button" className="btn p" disabled={saving} onClick={submit}>{saving ? 'Saving…' : 'Save'}</button>
        <button type="button" className="btn" onClick={onCancel}>Cancel</button>
        {item && <button type="button" className="btn s" onClick={remove} style={{ marginLeft: 'auto' }}>Remove</button>}
      </div>
    </div>
  );
}


// ─── Claude's data pack (To-Do 14, 2026-09-29) ────────────────────────────────
// One spreadsheet (CSV: Section, Measure, Value, Notes) with this week's
// numbers, goals, hours, costs, decisions, the next 7 days of events and
// testimonials added in the last 7 days — for Jenn to attach in her Monday
// chat with CB, who can't see the system directly.
async function downloadDataPack(d, planData) {
  const rows = [['Section', 'Measure', 'Value', 'Notes']];
  const add = (section, measure, value, notes = '') => rows.push([section, measure, value ?? '', notes]);
  const today = new Date().toISOString().slice(0, 10);
  add('About', 'Data pack date', today, 'Counting of visits/posts began Sept 28, 2026');
  if (d) {
    const m = d.members || {};
    const last = (arr) => (arr && arr.length ? arr[arr.length - 1] : null);
    const prev = (arr) => (arr && arr.length > 1 ? arr[arr.length - 2] : null);
    add('This week', 'Visitors to 365reset.com', last(d.visitsByWeek)?.n, `week of ${last(d.visitsByWeek)?.label || ''}`);
    add('Last week', 'Visitors to 365reset.com', prev(d.visitsByWeek)?.n, `week of ${prev(d.visitsByWeek)?.label || ''}`);
    add('This week', 'New members', last(d.joinsByWeek)?.n);
    add('Last week', 'New members', prev(d.joinsByWeek)?.n);
    const f = d.marketingFunnel || {};
    add('This month', 'Visited → came back → started signing up → joined', `${f.visited} → ${f.cameBack} → ${f.started} → ${f.joined}`);
    add('Members', 'Monthly paying (break-even)', m.breakEven?.monthly, `goal ${planData?.plan?.goal_ladder?.breakEven ?? m.breakEven?.goal}`);
    if (m.plans) Object.entries(m.plans).forEach(([k, v]) => add('Members by plan', k, v));
    add('Members', 'Active in My Hub this week', m.weeklyActive?.count, `${m.weeklyActive?.pct}% of ${m.weeklyActive?.active}`);
    add('Email', 'Daily emails sent (last 24h)', m.nudgesSentToday);
    if (m.email?.today) add('Email', 'Opened / clicked into My Hub (today)', `${m.email.today.opened} / ${m.email.today.clickedHub}`, 'opens can run a little high');
    add('Referrals', 'Referred joins this month', d.referrals?.thisMonth, `${d.referrals?.fromAffiliates} affiliates, ${d.referrals?.fromMembers} members`);
    add('Referrals', 'Thank-yous owed', d.referrals?.thanks?.owed);
    (d.posts || []).forEach(p => add('Tagged posts (since Sept 28)', p.post, `${p.visits} visits, ${p.started} started, ${p.joins} joined`, `${p.platform || ''} ${p.campaign || ''}`.trim()));
    (d.joinsByPlatform || []).forEach(p => add('Joins by platform (this month)', p.platform, p.n));
    (d.howHeard || []).forEach(h => add('How did you hear about us', h.answer, h.n));
    (m.asks || []).forEach(a => add('What members ask for (this quarter)', a.label, a.n));
    (m.hubSections || []).forEach(h => add('Most-used Hub pages (this month)', h.section, h.visits));
    if (m.leaving) ['Price', 'No time', 'Not using it', 'Life change', 'Other'].forEach((k, i) => add('Why members leave', k, m.leaving.reasons[i]));
  }
  if (planData) {
    const { plan, perCampaign, decisions, costs } = planData;
    const L = plan.goal_ladder;
    add('Goals', 'Break-even', L.breakEven, L.confirmed ? '' : "CB's suggestion");
    ['quarter', 'month', 'week', 'today'].forEach(k => add('Goals', k, L[k].target, L[k].note));
    Object.entries(plan.campaign_goals).filter(([k]) => k !== 'confirmed' && k !== 'updatedAt').forEach(([k, v]) => add('Campaign goals', k, v));
    (plan.week_focus.items || []).forEach((t, i) => add("This week's focus", `${i + 1}`, t));
    ['who', 'message', 'tactics', 'notDoing'].forEach(k => add('Strategy', k, plan.strategy[k]));
    perCampaign.forEach(p => add('Time & cost (this quarter)', p.campaign, `${p.hoursQuarter} h`, `joins ${p.joins ?? 'n/a'} · joins/hour ${p.joinsPerHour ?? 'n/a'} · spent $${p.spent} · per member ${p.costPerMember ?? 'n/a'}`));
    costs.forEach(c => add('Costs (this quarter)', `${c.spent_on} · ${c.campaign}`, c.amount, c.note || ''));
    decisions.forEach(x => add('Decision log', x.decided_on, x.decision, `${RESULT_LABELS[x.result]}${x.why ? ` · why: ${x.why}` : ''}`));
  }
  try {
    const events = (await api.get('/admin/hub-events')).data || [];
    const soon = Date.now() + 7 * 864e5;
    events.filter(e => !e.canceled_at && e.event_datetime && new Date(e.event_datetime) >= new Date() && new Date(e.event_datetime) <= soon)
      .forEach(e => add('Upcoming events (next 7 days)', new Date(e.event_datetime).toLocaleString('en-CA', { timeZone: 'America/Halifax', weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }), e.title));
  } catch { add('Upcoming events (next 7 days)', 'Could not load', ''); }
  try {
    const ts = (await api.get('/admin/sales-marketing/testimonials')).data.testimonials || [];
    ts.filter(t => new Date(t.created_at) > new Date(Date.now() - 7 * 864e5))
      .forEach(t => add('New testimonials (last 7 days)', t.person_name, t.quote, (t.permission_scope || []).length ? `approved for ${t.permission_scope.join(', ')}` : 'permission not recorded'));
  } catch { /* skip */ }
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const blob = new Blob(['\ufeff' + rows.map(r => r.map(esc).join(',')).join('\n')], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `365reset-data-pack-${today}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}


// ─── What's out there (live, Jenn 2026-10-13) ──────────────────────────────────
// One tile per post (the same post tweaked for each platform): its picture, then
// 365reset's platforms and Jenn's own accounts, each with the numbers from that
// platform's own tracked link — so she sees at a glance what works, and where.
const PLAT = {
  facebook: ['f', '#1877F2', 'Facebook'], instagram: ['IG', '#C13584', 'Instagram'], tiktok: ['TT', '#111111', 'TikTok'],
  threads: ['@', '#333333', 'Threads'], bluesky: ['BS', '#1185FE', 'Bluesky'], linkedin: ['in', '#0A66C2', 'LinkedIn'], pinterest: ['P', '#E60023', 'Pinterest'],
};
function PlatformStat({ s }) {
  const [glyph, color, name] = PLAT[s.platform] || ['?', '#888', s.platform];
  const quiet = !s.clicks && !s.visits;
  return (
    <div title={`${name}: ${s.clicks} clicks · ${s.visits} visits · ${s.price} reached the price · ${s.joined} joined (365reset.com/go/${s.code})`}
      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 46, opacity: quiet ? 0.55 : 1 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)' }}>{s.visits}</div>
      <div style={{ fontSize: 10, color: 'var(--ink-3)', marginBottom: 3 }}>{s.clicks} clk{s.price ? ` · ${s.price}$` : ''}{s.joined ? ` · ${s.joined}★` : ''}</div>
      {s.url
        ? <a href={s.url} target="_blank" rel="noopener noreferrer" aria-label={`Open the ${name} post`} style={{ textDecoration: 'none' }}><span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26, borderRadius: 7, background: color, color: '#fff', fontSize: 11, fontWeight: 700 }}>{glyph}</span></a>
        : <span aria-label={name} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26, borderRadius: 7, background: color, color: '#fff', fontSize: 11, fontWeight: 700 }}>{glyph}</span>}
    </div>
  );
}
const dayName = { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' };
function PostGridPage() {
  const [weeks, setWeeks] = useState(4);
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    setData(null);
    api.get('/admin/sales-marketing/post-grid', { params: { weeks } }).then(r => setData(r.data)).catch(() => setErr('Could not load the posts.'));
  }, [weeks]);
  return (
    <section>
      <h2 className="ptitle">What's out there</h2>
      <p className="pq">Every post, with how it did on each platform. Big number = people who reached 365reset.com from it. Small: link clicks, $ = reached the price, ★ = joined. Faded = nothing yet.</p>
      <div className="row" style={{ marginBottom: 12 }}>
        {[2, 4, 8].map(w => <button key={w} type="button" className={`btn s ${weeks === w ? 'p' : ''}`} onClick={() => setWeeks(w)}>Last {w} weeks</button>)}
      </div>
      {err && <div className="note">{err}</div>}
      {!data ? <p className="pq">Loading…</p> : data.posts.length === 0 ? <div className="empty">No tracked posts in this time yet.</div> : (
        <div className="grid g3">
          {data.posts.map(g => (
            <div className="card" key={g.key} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {g.image
                ? <img src={g.image} alt={g.imageAlt || `Post: ${g.label}`} style={{ width: '100%', aspectRatio: '3 / 4', objectFit: 'cover', borderRadius: 10, background: 'var(--cream)' }} />
                : <div style={{ width: '100%', aspectRatio: '3 / 4', borderRadius: 10, background: 'var(--cream)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ink-3)', fontSize: 12 }}>No picture saved yet</div>}
              <div>
                <p className="q" style={{ margin: 0, textTransform: 'capitalize' }}>{g.label}</p>
                <p className="qs" style={{ margin: 0 }}>{dayName[g.day]}{g.postAt ? ` · ${new Date(g.postAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}` : g.week ? ` · week of ${g.week}` : ''}</p>
              </div>
              {g.brand.length > 0 && <div><div className="qs" style={{ margin: '0 0 4px' }}>365reset</div><div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>{g.brand.map(s => <PlatformStat key={s.code} s={s} />)}</div></div>}
              {g.jenn.length > 0 && <div><div className="qs" style={{ margin: '0 0 4px' }}>Jenn's accounts</div><div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>{g.jenn.map(s => <PlatformStat key={s.code} s={s} />)}</div></div>}
            </div>
          ))}
        </div>
      )}
      <div className="note" style={{ marginTop: 12 }}>From Oct 21, link clicks count people only (robots that preview links are left out). Clicks before then still include robots, so visits are the truer number. Your personal Facebook views (typed in on Platform by platform) aren't tracked links, so they show there, not here.</div>
    </section>
  );
}

// Simple horizontal bars: rows = [[label, n], ...]
function HBars({ rows }) {
  const max = Math.max(1, ...rows.map(r => r[1]));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {rows.map(([label, n]) => (
        <div key={label} style={{ display: 'grid', gridTemplateColumns: 'minmax(110px, 40%) 1fr 32px', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13 }}>{label}</span>
          <div style={{ background: 'var(--cream)', borderRadius: 6, height: 14 }}><div style={{ width: `${(n / max) * 100}%`, background: '#C4714A', height: 14, borderRadius: 6 }} /></div>
          <b style={{ fontSize: 13, textAlign: 'right' }}>{n}</b>
        </div>
      ))}
    </div>
  );
}

// ─── Interested people (live, Jenn 2026-10-13) ─────────────────────────────────
// Real people who put their hand up and that we can reply to (quiz takers who
// asked for emails, affiliate/partner applications), plus a count of anonymous
// visitors who reached the price. Jenn moves people along; Joined is automatic.
const LEAD_STAGES = [['new', 'New'], ['replied', 'Replied'], ['warming', 'Warming up'], ['invited', 'Invited to join'], ['joined', 'Joined']];
const waitedFor = (iso) => {
  const h = Math.max(0, Math.round((Date.now() - new Date(iso)) / 3600000));
  return h < 48 ? `${h} hour${h === 1 ? '' : 's'}` : `${Math.round(h / 24)} days`;
};
function InterestedPeoplePage() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [showNotNow, setShowNotNow] = useState(false);
  const load = useCallback(() => {
    api.get('/admin/sales-marketing/interested').then(r => setData(r.data)).catch(() => setErr('Could not load interested people.'));
  }, []);
  useEffect(() => { load(); }, [load]);
  const move = async (p, stage) => {
    try { await api.put(`/admin/sales-marketing/interested/${p.source}/${p.id}`, { stage }); load(); }
    catch { setErr('That didn\'t save. Please try again.'); }
  };
  if (err && !data) return <section><h2 className="ptitle">Interested people</h2><div className="note">{err}</div></section>;
  if (!data) return <section><h2 className="ptitle">Interested people</h2><p className="pq">Loading…</p></section>;
  const { stats, people, anonymousReachedPrice: anon } = data;
  const byStage = (k) => people.filter(p => p.stage === k);
  const nextOf = (k) => { const i = LEAD_STAGES.findIndex(([s]) => s === k); return LEAD_STAGES[i + 1]?.[0]; };
  return (
    <section>
      <h2 className="ptitle">Interested people</h2>
      <p className="pq">Everyone who put their hand up and gave us a way to reply. Move each person along as you go.</p>
      <div className="grid g4">
        <Card live><p className="q">New this week</p><div className="bign">{stats.newThisWeek}</div></Card>
        <Card live><p className="q">Waiting for a reply</p><div className="bign">{stats.waiting}</div>{stats.oldestWaiting && <div className="lbl">Oldest: {waitedFor(stats.oldestWaiting)}</div>}</Card>
        <Card live><p className="q">Became members</p><div className="bign">{stats.becameMembers}</div></Card>
        <Card live><p className="q">Average time to join</p><div className="bign">{stats.avgDaysToJoin ?? '–'} {stats.avgDaysToJoin != null && <small>days</small>}</div></Card>
      </div>
      <div className="note" style={{ marginTop: 12 }}>
        <b>Also interested, but anonymous:</b> {anon.week} {anon.week === 1 ? 'visitor' : 'visitors'} reached the price on 365reset.com this week ({anon.month} in the last 30 days).
        We don't have a name or email for them, so they can't be on the board below. (Includes some of our own tests.)
      </div>
      <Card live style={{ marginTop: 16 }}><p className="q">What's holding people back</p>
        <p className="qs">One-tap answers to "Not sure yet?" under the payment form on 365reset.com · last 30 days</p>
        {(data.barriers || []).length === 0 ? <div className="empty">No answers yet.</div> : (
          <HBars rows={data.barriers.map(r => [({ price: 'The price', time: "No time", not_sure_fit: "Not sure it's for me", try_first: 'Want to try it first', other: 'Something else' })[r.barrier] || r.barrier, r.n])} />
        )}</Card>
      <Card live style={{ marginTop: 16 }}><p className="q">Moving people toward joining</p><p className="qs">Click a name to email them. "Joined" fills in by itself when their email becomes a member.</p>
        <div className="kan">
          {LEAD_STAGES.map(([k, label]) => (
            <div className="col" key={k}><h4>{label} <span>{byStage(k).length}</span></h4>
              {byStage(k).map(p => (
                <div className="pc" key={p.key}>
                  <a href={`mailto:${p.email}`} style={{ fontWeight: 600, color: 'inherit' }}>{p.name || p.email}</a><br />
                  {p.from}<br />
                  <span className={k === 'new' ? 'w' : ''}>{k === 'joined' ? `Joined ${new Date(p.joinedAt).toLocaleDateString()}` : `Since ${new Date(p.createdAt).toLocaleDateString()}`}</span>
                  {k !== 'joined' && (
                    <div className="row" style={{ marginTop: 6, gap: 6 }}>
                      {nextOf(k) && nextOf(k) !== 'joined' && <button type="button" className="btn s" onClick={() => move(p, nextOf(k))}>Move to {LEAD_STAGES.find(([s]) => s === nextOf(k))[1]} →</button>}
                      {k !== 'new' && <button type="button" className="btn s" onClick={() => move(p, LEAD_STAGES[LEAD_STAGES.findIndex(([s]) => s === k) - 1][0])}>← Back</button>}
                      <button type="button" className="btn s" onClick={() => move(p, 'not_now')}>Not now</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
        {byStage('not_now').length > 0 && (
          <div style={{ marginTop: 10 }}>
            <button type="button" className="btn s" onClick={() => setShowNotNow(v => !v)}>{showNotNow ? 'Hide' : 'Show'} "Not now" ({byStage('not_now').length})</button>
            {showNotNow && byStage('not_now').map(p => (
              <div key={p.key} className="qs" style={{ marginTop: 6 }}>{p.name || p.email} · {p.from} <button type="button" className="btn s" onClick={() => move(p, 'new')}>Back to New</button></div>
            ))}
          </div>
        )}
        {err && <div className="note">{err}</div>}
      </Card>
    </section>
  );
}

// ─── This week's focus — a checkable list, stacked top to bottom (Jenn,
// 2026-09-29), with the Threadline hearts when she ticks one off. Ticks are
// saved, so they stay ticked; setting a new focus in Goals & plan starts fresh.
function FocusCard({ planData, onChange, onGo }) {
  const focus = planData?.plan?.week_focus;
  const [done, setDone] = useState(null);
  const items = focus?.items || [];
  const ticks = done || focus?.done || items.map(() => false);
  const toggle = async (i) => {
    const next = items.map((_, j) => (j === i ? !ticks[j] : !!ticks[j]));
    setDone(next);
    if (next[i]) fireHearts();
    try { await api.put('/admin/sales-marketing/plan/week_focus', { items, done: next }); onChange(); } catch { setDone(null); }
  };
  return (
    <div className="card">
      <div><Status live /></div>
      <p className="q">This week's focus</p>
      <p className="qs">{focus && !focus.confirmed ? "CB's suggestion — set your own in Goals & plan" : 'From Goals & plan'}</p>
      {items.length === 0 ? <div className="empty">No focus set for this week yet.</div> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {items.map((t, i) => (
            <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, minHeight: 44, padding: '8px 12px', borderRadius: 12, background: ticks[i] ? 'var(--forest-soft)' : 'var(--cream)', cursor: 'pointer' }}>
              <input type="checkbox" checked={!!ticks[i]} onChange={() => toggle(i)} style={{ width: 22, height: 22, accentColor: '#4A6355', flex: 'none' }} />
              <span style={{ fontSize: 15, textDecoration: ticks[i] ? 'line-through' : 'none', color: ticks[i] ? 'var(--ink-3)' : 'var(--ink)' }}>{t}</span>
            </label>
          ))}
        </div>
      )}
      <button type="button" className="more" style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer' }} onClick={() => onGo('goals')}>Change this week's focus →</button>
    </div>
  );
}
